import { differenceInDays } from "date-fns";
import i18next from "i18next";
import { SimpleAlert } from "../components/Alerts/Alerts";
import axios from "../lib/axios";
import {
  getFormattedCampaign,
  getLanguageFormatted,
  getObjectError,
} from "../utils/string";

// Sentry se gestiona ahora desde LaunchError
import { getPathDatabaseByRole, renewToken } from "./auth";
import { isPrivilegedLoginFromStorage } from "../utils/privilegedLoginStorage";
import {
  getMetaTrackingCookies,
  META_COOKIE_PATTERN,
} from "../services/tracking";
import {
  COPY,
  ERROR,
  ERROR_OCCURRED,
  ERROR_PROCESSING_CAMPAIGNS,
  ERROR_DESCRIPTION_GENERIC,
  ERROR_GETTING_URL_INFORMATION_BY_PROTECTED,
  ERROR_GETTING_URL_META_DATA_AND_IMAGES,
  NO_PIXELS_FOUND,
  MISSING_PARAMETERS,
  CAMPAIGN_ALREADY_BEING_CREATED,
  CAMPAIGN_ALREADY_BEING_UPDATED,
} from "../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_ACTION_STATUS,
  ADS_CAMPAIGN_ENTITIES,
  ADS_CAMPAIGN_MEDIA_FROM_PHYLLO,
  ADS_CAMPAIGN_STATUS_ACTIVE,
  ADS_CAMPAIGN_STATUS_DRAFT,
  ADS_CAMPAIGNS_DEMO,
  ADS_OBJECT_TYPES,
  ALERT_ICON_TYPE_ERROR,
  ALERTS_ASYNC_STATUS,
  GOOGLE,
  LINKEDIN,
  META,
  NPS_RATING_ACTION_CREATE,
  NPS_RATING_ACTION_PUBLISH,
  NPS_RATING_ACTION_ACTIVE_7_DAYS,
  NPS_RATING_ACTION_ACTIVE_30_DAYS,
  NPS_RATING_ACTION_GENERATE_9_CONTENTS,
  NPS_COOL_DOWN_DAYS,
  CHECKLIST_CREATE_CAMPAIGN,
  CHECKLIST_PUBLISH_CONTENT,
  CHECKLIST_CREATE_CONTENT,
  CHECKLIST_BRAND_SETUP,
  OMIT_CAMPAIGNS_FROM_SPECIFIC_ADVERTISER_FROM_AGENCY_COUNT,
  RECURRENT_MULTIPLIER,
  SORT_DIRECTION,
  SORTABLE_COLUMNS,
  STATUS_SORT_ORDER,
  ADS_ACTION_TYPES,
  PLAN_TYPE_CREDITS,
  BRAND_INSIGHT_ALLOWLIST_FIELDS,
} from "../utils/constants";
import types from "../types";
import { shouldUseAdsCampaignDemoDateRange } from "../utils/adsCampaignDemoDateRange";
import { db } from "../lib/firebase";
import { countAppliedOptimizationsFromCampaignLogs } from "../pages/GlobalReport/utils/countAppliedOptimizationsFromCampaignLogs";
import {
  get,
  limitToFirst,
  orderByChild,
  push,
  query,
  ref,
  remove,
  set,
  startAfter,
  update,
  getDatabase,
  startAt,
  endAt,
  equalTo,
  off,
} from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import {
  ERRORS_BILLING,
  isAbortError,
  LaunchError,
  resolveChatAgentErrorMessage,
  CREATIVE_JOB_TYPES,
} from "../utils/errors";
import _ from "lodash";
import { formatDateForStatistics, waitDelay } from "../utils/date";
import { replaceUndefinedWithNull } from "../utils/forms";
import { isSameLeadFormID } from "../utils/linkedInLeadFormLanguage";
import {
  getCreateLeadFormFailureCode,
  reportCreateLeadFormAdsCampaignsFailure,
} from "../utils/createLeadFormSentry";
import { differenceInMinutes, subMonths } from "date-fns";
import { applyFiltersImportAdsCampaigns } from "../services/adsCampaigns";
import { getIntegrationKey, getUserEmail, getUserID } from "./getters";
import {
  addNotification,
  openIntegrationNotFoundModalIfNeeded,
  promoteCreativeNotification,
  setPendingIntegrationFormSync,
  updateNotification,
} from "./alerts";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import {
  buildKpiTargetCpaFingerprint,
  isKpiTargetCpaCacheFresh,
} from "../utils/kpiTargetCpaCheck";
import { migrateCreativeJobProgress } from "../utils/creativeJobProgressStorage";
import {
  mapUnifiedToLegacyCampaignsStatsMap,
  mapUnifiedToLegacySingleCampaignStats,
  mapUnifiedToLegacyTimeSeries,
  LEGACY_CAMPAIGNS_STATS_LEVELS,
  LEGACY_CAMPAIGNS_STATS_BREAKDOWNS,
  LEGACY_CAMPAIGNS_TIME_SERIES_BREAKDOWNS,
  GLOBAL_REPORT_STATS_WITH_ADS_LEVELS,
  GLOBAL_REPORT_STATS_WITH_ADS_BREAKDOWNS,
  LEGACY_SINGLE_CAMPAIGN_STATS_LEVELS,
  LEGACY_SINGLE_CAMPAIGN_STATS_BREAKDOWNS,
} from "../utils/unifiedCampaignStatisticsAdapters";
import { createCreativeJobAndWaitForResponse } from "./jobs";
import { toBulkContentIDs } from "../utils/bulkCampaignContent";
import {
  getBlockedBrandFromText,
  shouldApplyBlockedBrandsValidation,
} from "../pages/Settings/BrandSetup/utils/blockedBrandsValidation";

const BULK_CAMPAIGNS_JOB_TYPE = {
  analyze: "analyzeBulkCampaigns",
  generate: "generateBulkCampaigns",
};

const BULK_CAMPAIGNS_EXPECTED_ERROR_CODES = [
  "unsupportedFileType",
  "emptyFile",
  "fileTooLarge",
  "platformNotDetected",
  "platformNotSupported",
  "campaignTypeNotSupported",
  "tooManyContents",
];

const BULK_CAMPAIGNS_GENERATION_EXPECTED_ERROR_CODES = ["accountsUnavailable"];

export const setAdsCampaignStatusOverride = ({ campaignID, status }) => ({
  type: types.SET_ADS_CAMPAIGN_STATUS_OVERRIDE,
  payload: { campaignID, status },
});

export const removeAdsCampaignStatusOverrides = (campaignIDs = []) => ({
  type: types.REMOVE_ADS_CAMPAIGN_STATUS_OVERRIDES,
  payload: campaignIDs,
});

const isSameDay = (timestamp) => {
  const savedDate = new Date(timestamp);
  const today = new Date();

  return (
    savedDate.getFullYear() === today.getFullYear() &&
    savedDate.getMonth() === today.getMonth() &&
    savedDate.getDate() === today.getDate()
  );
};

const inFlightCurrencyTaxRequests = new Map();

export const startGetCurrencyTax = (currency) => async (dispatch) => {
  if (!currency || currency === "USD") {
    return { ok: true, data: 1 };
  }

  const inFlightRequest = inFlightCurrencyTaxRequests.get(currency);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const requestPromise = (async () => {
    try {
      const dbRef = ref(db, `currenciesTaxesUSD/${currency}`);
      const snapshot = await get(dbRef);

      if (snapshot.exists()) {
        const dataFromDB = snapshot.val();
        if (isSameDay(dataFromDB.lastUpdateTime) && dataFromDB.tax != null) {
          return { ok: true, data: dataFromDB.tax };
        }
      }

      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getCurrencies")}/getCurrencies`,
        data: {
          currency: currency,
          toCurrencies: ["USD"],
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data?.ok) {
        return { ok: false };
      }

      const tax = data?.data?.tax;
      return { ok: true, data: tax };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetCurrencyTax",
          context: { currency },
          tags: { action: "getCurrencyTax" },
          level: "error",
        })
      );
      return { ok: false };
    } finally {
      inFlightCurrencyTaxRequests.delete(currency);
    }
  })();

  inFlightCurrencyTaxRequests.set(currency, requestPromise);
  return requestPromise;
};

// Actions for campaign publishing state (maintain compatibility)
export const setPublishingCampaignState = (payload) => ({
  type: types.SET_PUBLISHING_CAMPAIGN_STATE,
  payload,
});

export const clearPublishResult = () => ({
  type: types.CLEAR_PUBLISH_RESULT,
});

// New action to check if an alert exists for a campaignID
export const checkAndAddPublishingCampaign =
  (campaignData) => (dispatch, getState) => {
    const state = getState();
    const existingAlert = state.alerts.notifications.find((notification) => {
      if (notification.campaignID !== campaignData.campaignID) {
        return false;
      }

      if (campaignData.isGenerating) {
        const isTerminal =
          notification.status === ALERTS_ASYNC_STATUS.ERROR ||
          notification.status === ALERTS_ASYNC_STATUS.SUCCESS;
        return !isTerminal;
      }

      return notification.id === campaignData.campaignID;
    });
    const alertStatus = campaignData.status || ALERTS_ASYNC_STATUS.LOADING;

    if (existingAlert) {
      const isErrorStatus = alertStatus === ALERTS_ASYNC_STATUS.ERROR;

      // If exists, update the existing alert
      dispatch(
        updateNotification(existingAlert.id, {
          isCreating: campaignData.isCreating || false,
          isGenerating: campaignData.isGenerating || false,
          status: alertStatus,
          message: campaignData.message ?? null,
          progress: campaignData.progress || 0,
          platformGeneratingData: campaignData.platformGeneratingData || null,
          currentCampaign: campaignData.currentCampaign || null,
          newPlatforms: campaignData.newPlatforms || [],
          buttonAction: campaignData.buttonAction || null,
          platform: campaignData.platform || null,
          ...(campaignData.isGenerating
            ? {
                jobType: CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform,
              }
            : {}),
          ...(isErrorStatus
            ? {}
            : { failedCampaigns: [], errorsCampaigns: {} }),
          timestamp: Date.now(),
        })
      );
    } else {
      // If doesn't exist, create a new alert
      dispatch(
        addNotification({
          type: "campaign",
          id: campaignData.campaignID,
          campaignID: campaignData.campaignID,
          isCreating: campaignData.isCreating || false,
          isGenerating: campaignData.isGenerating || false,
          status: campaignData.status || ALERTS_ASYNC_STATUS.LOADING,
          ...(campaignData.message ? { message: campaignData.message } : {}),
          progress: campaignData.progress || 0,
          failedCampaigns: campaignData.failedCampaigns || [],
          errorsCampaigns: campaignData.errorsCampaigns || {},
          currentCampaign: campaignData.currentCampaign || null,
          buttonAction: campaignData.buttonAction || null,
          platform: campaignData.platform || null,
          platformGeneratingData: campaignData.platformGeneratingData || null,
          newPlatforms: campaignData.newPlatforms || [],
          ...(campaignData.isGenerating
            ? {
                jobType: CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform,
              }
            : {}),
          timestamp: Date.now(),
        })
      );
    }
  };

// Save campaign as draft
export const startSaveDraftCampaign =
  ({ campaignID, campaign }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const draftRef = ref(db, `adsCampaignsDraft/${userID}/${campaignID}`);

      const formattedCampaign = replaceUndefinedWithNull(
        getFormattedCampaign({
          data: campaign,
          format: "save",
        })
      );
      await set(draftRef, formattedCampaign);

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveDraftCampaign",
          context: { campaignID },
          tags: { action: "saveDraftCampaign" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

// Get draft campaign
export const startGetDraftCampaign =
  ({ campaignID }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const draftRef = ref(db, `adsCampaignsDraft/${userID}/${campaignID}`);

      const snapshot = await get(draftRef);

      if (!snapshot.exists()) {
        return { ok: false };
      }

      const formattedCampaign = getFormattedCampaign({
        data: snapshot.val(),
        format: "load",
      });

      return {
        ok: true,
        data: formattedCampaign,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetDraftCampaign",
          context: { campaignID },
          tags: { action: "getDraftCampaign" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

// Delete draft campaign
export const startDeleteDraftCampaign =
  ({ campaignID }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const draftRef = ref(db, `adsCampaignsDraft/${userID}/${campaignID}`);

      await remove(draftRef);

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteDraftCampaign",
          context: { campaignID },
          tags: { action: "deleteDraftCampaign" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startSaveAdsCampaignMetricsConfiguration =
  ({ campaignID, platform, metrics }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(
        db,
        `adsCampaignsMetricsConfigurations/${userID}/${campaignID}/${platform}`
      );
      await set(dbRef, metrics);

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveAdsCampaignMetricsConfiguration",
          context: { campaignID, platform, metrics },
          tags: { action: "saveAdsCampaignMetricsConfiguration" },
          level: "error",
        })
      );
      return false;
    }
  };

export const startGetAdsCampaignMetricsConfiguration =
  ({ campaignID, platform }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(
        db,
        `adsCampaignsMetricsConfigurations/${userID}/${campaignID}/${platform}`
      );
      const snapshot = await get(dbRef);

      if (!snapshot.exists()) {
        return null;
      }

      return snapshot.val();
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdsCampaignMetricsConfiguration",
          context: { campaignID, platform },
          tags: { action: "getAdsCampaignMetricsConfiguration" },
          level: "error",
        })
      );

      return null;
    }
  };
export const startGetHomeMetricsConfiguration = () => async (dispatch) => {
  try {
    const { pathBase } = dispatch(getPathDatabaseByRole());

    const dbRef = ref(db, `${pathBase}/homeMetricsConfiguration`);
    const snapshot = await get(dbRef);

    if (!snapshot.exists()) {
      return { ok: false };
    }

    return { ok: true, data: snapshot.val() };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetHomeMetricsConfiguration",
        context: {},
        tags: { action: "getHomeMetricsConfiguration" },
        level: "error",
      })
    );
    return { ok: false };
  }
};
export const startSetHomeMetricsConfiguration =
  ({ metricsConfiguration }) =>
  async (dispatch) => {
    try {
      const { pathBase } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(db, `${pathBase}/homeMetricsConfiguration`);
      const snapshot = await get(dbRef);

      if (snapshot.exists()) {
        await update(dbRef, metricsConfiguration);
      } else {
        await set(dbRef, metricsConfiguration);
      }

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSetHomeMetricsConfiguration",
          context: {},
          tags: { action: "setHomeMetricsConfiguration" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startHasOneCampaign = () => async (dispatch) => {
  try {
    const { pathCampaigns } = dispatch(getPathDatabaseByRole());

    const dbRef = ref(db, pathCampaigns);
    const q = query(dbRef, limitToFirst(1));
    const snapshot = await get(q);

    return snapshot.exists();
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startHasOneCampaign",
        context: {},
        tags: { action: "hasOneCampaign" },
        level: "error",
      })
    );
    return false;
  }
};
export const startGetDetailedReport =
  ({ campaignID }) =>
  async (dispatch) => {
    try {
      const { pathDetailedReports } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(db, `${pathDetailedReports}/${campaignID}`);
      const snapshot = await get(dbRef);

      if (!snapshot.exists()) {
        return {
          ok: false,
        };
      }

      return { ok: true, data: snapshot.val() };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetDetailedReport",
          context: { campaignID },
          tags: { action: "getDetailedReport" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startListenerAdsContentsUploaded =
  ({
    onReferenceAvailable = () => {},
    onUpdatedDataCallback = () => {},
    campaignID,
  }) =>
  async (dispatch) => {
    try {
      const { pathAdsContentsUploaded } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `${pathAdsContentsUploaded}/${campaignID}`);
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        const data = snapshot.val();
        onUpdatedDataCallback(data);
      });
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerAdsContentsUploaded",
          context: { campaignID },
          tags: { action: "listenerAdsContentsUploaded" },
          level: "error",
        })
      );
      return false;
    }
  };
export const startListenerAdsPlatformsBanners =
  ({ onUpdatedDataCallback = () => {} }) =>
  async (dispatch) => {
    try {
      const dbRefPlatformsBanners = ref(db, `adsPlatformsBanners`);
      onValue(dbRefPlatformsBanners, (snapshot) => {
        const data = snapshot.val();
        onUpdatedDataCallback(data);
      });
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerAdsPlatformsBanners",
          context: {},
          tags: { action: "listenerAdsPlatformsBanners" },
          level: "error",
        })
      );
      return false;
    }
  };

export const startDeleteAdMediaID =
  ({ adID, index }) =>
  async (dispatch) => {
    try {
      const { pathAds } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `${pathAds}/${adID}/mediaIDs/${index}`);
      await set(dbRef, "");
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteAdMediaID",
          context: { adID, index },
          tags: { action: "deleteAdMediaID" },
          level: "error",
        })
      );
      return false;
    }
  };
export const startSaveReportOptimizationsDone =
  ({ campaignID, optimizationsDone, platform }) =>
  async (dispatch) => {
    try {
      if (ADS_CAMPAIGNS_DEMO.includes(campaignID)) {
        await waitDelay(5000);
        return true;
      }

      const { pathCampaigns } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(
        db,
        `${pathCampaigns}/${campaignID}/report/optimizations/byPlatform/${platform}/optimizationsDone`
      );
      await set(dbRef, optimizationsDone);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveReportOptimizationsDone",
          context: { campaignID, platform, optimizationsDone },
          tags: { action: "saveReportOptimizationsDone" },
          level: "error",
        })
      );
      return false;
    }
  };

export const startDeleteAdsContentUploaded =
  ({ campaignID, contentID }) =>
  async (dispatch) => {
    try {
      const { pathAdsContentsUploaded } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(
        db,
        `${pathAdsContentsUploaded}/${campaignID}/${contentID}`
      );
      await remove(dbRef);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteAdsContentUploaded",
          context: { campaignID, contentID },
          tags: { action: "deleteAdsContentUploaded" },
          level: "error",
        })
      );
      return false;
    }
  };
export const startUpdateMediaUrlAd =
  ({ adID, mediaUrl, index }) =>
  (dispatch) => {
    try {
      const { pathAds } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `${pathAds}/${adID}/mediaUrls/${index}`);
      set(dbRef, mediaUrl);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUpdateMediaUrlAd",
          context: { adID, index },
          tags: { action: "updateMediaUrlAd" },
          level: "error",
        })
      );
      return false;
    }
  };
export const startUpdateImageUrlPlatformData =
  ({ campaignID, platform, imageUrl, index }) =>
  (dispatch) => {
    try {
      const { pathCampaigns } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(
        db,
        `${pathCampaigns}/${campaignID}/platformsData/${platform}/imageUrls/${index}`
      );
      set(dbRef, imageUrl);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUpdateImageUrlPlatformData",
          context: { campaignID, platform, index },
          tags: { action: "updateImageUrlPlatformData" },
          level: "error",
        })
      );
      return false;
    }
  };
export const startUpdateImageUrlAssetGroup =
  ({ assetsGroupID, imageUrl, index }) =>
  (dispatch) => {
    try {
      const { pathAssetsGroups } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(
        db,
        `${pathAssetsGroups}/${assetsGroupID}/imageUrls/${index}`
      );
      set(dbRef, imageUrl);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUpdateImageUrlAssetGroup",
          context: { assetsGroupID, index },
          tags: { action: "updateImageUrlAssetGroup" },
          level: "error",
        })
      );
      return false;
    }
  };
export const startUpdateMediaUrlAssetGroup =
  ({ assetsGroupID, mediaUrl, index }) =>
  (dispatch) => {
    try {
      const { pathAssetsGroups } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(
        db,
        `${pathAssetsGroups}/${assetsGroupID}/mediaUrls/${index}`
      );
      set(dbRef, mediaUrl);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUpdateMediaUrlAssetGroup",
          context: { assetsGroupID, index },
          tags: { action: "updateMediaUrlAssetGroup" },
          level: "error",
        })
      );
      return false;
    }
  };
export const startGetUserLanguage = () => async (dispatch, getState) => {
  try {
    const { pathUsers } = dispatch(getPathDatabaseByRole());
    const uid = getState().auth.user.uid;

    const userRef = ref(db, `${pathUsers}/${uid}`);
    const userSnapshot = await get(userRef);
    const userData = userSnapshot.val() || {};

    return userData.language || null;
  } catch (error) {
    console.log(error);
    dispatch(LaunchError(error, false));
    return null;
  }
};

export const startUpdateUserLanguage =
  (language) => async (dispatch, getState) => {
    try {
      const { pathUsers } = dispatch(getPathDatabaseByRole());
      const uid = getState().auth.user.uid;
      const formattedLanguage = getLanguageFormatted(language);

      const updates = {};
      if (formattedLanguage) {
        updates[`${pathUsers}/${uid}/language`] = formattedLanguage;
      }

      const dbRef = ref(db);
      await update(dbRef, updates);

      return true;
    } catch (error) {
      console.log(error);
      dispatch(LaunchError(error, false));
      return false;
    }
  };

export const startResetAdvertiserLanguage = () => ({
  type: types.RESET_ADVERTISER_LANGUAGE,
});

export const startSetAdvertiserLanguage = (language) => ({
  type: types.SET_ADVERTISER_LANGUAGE,
  payload: language || null,
});

export const startGetAdvertiserLanguage = () => async (dispatch) => {
  try {
    const { pathBrandInformation } = dispatch(getPathDatabaseByRole());
    const snapshot = await get(ref(db, `${pathBrandInformation}/language`));

    return { ok: true, language: snapshot.val() || null };
  } catch (error) {
    console.log(error);
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetAdvertiserLanguage",
        context: {},
        tags: { action: "getAdvertiserLanguage" },
        level: "error",
      })
    );
    return { ok: false, language: null };
  }
};

export const startSaveAdvertiserLanguage =
  ({ language }) =>
  async (dispatch) => {
    if (!language) {
      return { ok: false };
    }

    try {
      const { pathBrandInformation } = dispatch(getPathDatabaseByRole());
      const updates = {
        [`${pathBrandInformation}/language`]: language,
      };

      await update(ref(db), updates);

      dispatch(startSetAdvertiserLanguage(language));

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveAdvertiserLanguage",
          context: { language },
          tags: { action: "saveAdvertiserLanguage" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startGetUserHideMetricsAlert =
  () => async (dispatch, getState) => {
    try {
      const { pathUsers } = dispatch(getPathDatabaseByRole());
      const uid = getState().auth.user.uid;

      const userRef = ref(db, `${pathUsers}/${uid}/hideDelayMetricsAlert`);
      const snapshot = await get(userRef);
      const hideDelayMetricsAlert = snapshot.val() ?? false;

      dispatch({
        type: types.GET_USER_HIDE_METRICS_ALERT,
        payload: hideDelayMetricsAlert,
      });

      return hideDelayMetricsAlert;
    } catch (error) {
      console.log(error);
      dispatch(LaunchError(error, false));
      return false;
    }
  };

export const startUpdateUserHideMetricsAlert =
  (value) => async (dispatch, getState) => {
    try {
      const { pathUsers } = dispatch(getPathDatabaseByRole());
      const uid = getState().auth.user.uid;

      const updates = {
        [`${pathUsers}/${uid}/hideDelayMetricsAlert`]: Boolean(value),
      };

      const dbRef = ref(db);
      await update(dbRef, updates);

      dispatch({
        type: types.SET_USER_HIDE_METRICS_ALERT,
        payload: Boolean(value),
      });

      return true;
    } catch (error) {
      console.log(error);
      dispatch(LaunchError(error, false));
      return false;
    }
  };

export const startSaveSessionDataUser = () => async (dispatch, getState) => {
  try {
    const language = getLanguageFormatted(i18next.language);
    const { pathUsers, pathAgencies } = dispatch(getPathDatabaseByRole());
    const uid = getState().auth.user.uid;
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "";
    const geoIP = getState().user.geoIP;
    const skipLastLogin =
      getState().auth.skipLastLogin || isPrivilegedLoginFromStorage({ uid });

    const updates = {};
    const metaTrackingUpdates = {};
    if (language) updates[`${pathUsers}/${uid}/language`] = language;
    if (userAgent) updates[`${pathUsers}/${uid}/userAgent`] = userAgent;
    if (geoIP) updates[`${pathUsers}/${uid}/geoIP`] = geoIP;

    if (!skipLastLogin) {
      const userRef = ref(db, `${pathUsers}/${uid}`);
      const userSnapshot = await get(userRef);
      const userData = userSnapshot.val() || {};
      const currentLastLogins = userData.lastLogins || [];
      const currentTimestamp = new Date().getTime();
      const updatedLastLogins = [...currentLastLogins, currentTimestamp];
      const thirtyDaysAgo = currentTimestamp - 30 * 24 * 60 * 60 * 1000;
      const filteredLastLogins = updatedLastLogins.filter(
        (timestamp) => timestamp > thirtyDaysAgo
      );
      updates[`${pathUsers}/${uid}/lastLogins`] = filteredLastLogins;
      updates[`${pathAgencies}/lastLogin`] = currentTimestamp;

      // Latest _fbp/_fbc for the Purchase sent from the Stripe webhook (CAPI).
      // Not in privileged sessions: those cookies belong to the staff browser.
      const metaCookies = getMetaTrackingCookies();
      ["fbp", "fbc"].forEach((key) => {
        const value = metaCookies[key];
        if (
          META_COOKIE_PATTERN.test(value || "") &&
          value !== userData.tracking?.meta?.[key]
        ) {
          metaTrackingUpdates[`${pathUsers}/${uid}/tracking/meta/${key}`] =
            value;
        }
      });
    }

    if (Object.keys(updates).length === 0) {
      return true;
    }

    const dbRef = ref(db);
    await update(dbRef, updates);

    // Separate write: a rejected tracking value must not drop the session data.
    if (Object.keys(metaTrackingUpdates).length > 0) {
      await update(dbRef, metaTrackingUpdates).catch((error) => {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "startSaveSessionDataUser",
            capture: "exception",
            tags: { source: "rtdb" },
          })
        );
        console.log("Error saving Meta tracking cookies", error);
      });
    }

    return true;
  } catch (error) {
    console.log(error);
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startSaveLanguageUser",
        context: {},
        tags: { action: "saveLanguageUser" },
        level: "error",
      })
    );
    return false;
  }
};
export const startGetAdsCampaignsIDsByAgency =
  (onReferenceAvailable = () => {}) =>
  async (dispatch, getState) => {
    try {
      const subscriptionPlan = getState()?.payments?.subscriptionPlan;
      const renewDate = getState()?.payments?.subscriptionPlanInfo?.renewDate;
      const recurrent = getState()?.payments?.subscriptionPlanInfo?.recurrent;

      const { agencyID } = dispatch(getPathDatabaseByRole());

      if (!subscriptionPlan || !renewDate) {
        const dbRef = query(ref(db, `adsCampaignsIDsByAgency/${agencyID}`));

        onValue(dbRef, (snapshot) => {
          const data = snapshot.val() || {};
          const campaignIDs = Object.keys(data);
          dispatch(getAdsCampaignsIDsByAgency(campaignIDs));
        });

        onReferenceAvailable(dbRef);
        return { ok: true };
      }

      const agoTime = subMonths(
        new Date(renewDate),
        RECURRENT_MULTIPLIER[recurrent] || 1
      ).getTime();
      const dbRef = query(
        ref(db, `adsCampaignsIDsByAgency/${agencyID}`),
        orderByChild("creationTime"),
        startAfter(agoTime)
      );

      onValue(dbRef, (snapshot) => {
        const data = snapshot.val() || {};
        let campaignIDs = Object.keys(data);

        // OMIT CAMPAIGNS - Exclude specific advertiser campaigns from agency count
        const advertiserToOmitCampaigns =
          OMIT_CAMPAIGNS_FROM_SPECIFIC_ADVERTISER_FROM_AGENCY_COUNT[agencyID];
        if (!_.isEmpty(advertiserToOmitCampaigns)) {
          campaignIDs = campaignIDs.filter(
            (campaignID) =>
              !advertiserToOmitCampaigns.includes(
                data?.[campaignID]?.advertiserID
              )
          );
        }

        dispatch(getAdsCampaignsIDsByAgency(campaignIDs));
      });

      onReferenceAvailable(dbRef);
    } catch (error) {
      console.log(error);
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdsCampaignsIDsByAgency",
          context: {},
          tags: { action: "getAdsCampaignsIDsByAgency" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
const getAdsCampaignsIDsByAgency = (data) => ({
  type: types.GET_ADS_CAMPAIGNS_IDS_BY_AGENCY,
  payload: data,
});

export const startGetAdsCampaigns =
  ({
    lastKey,
    onChangeLastKey = () => {},
    onLoadLastKey = () => {},
    limit = 20,
    sortConfig = { key: "creationTime", direction: SORT_DIRECTION.DESC },
    subValueSort,
    onChangeSubValueSort = () => {},
    isNewSubValueSort = false,
    restCampaigns = {},
  }) =>
  async (dispatch) => {
    try {
      const { pathCampaigns } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(db, pathCampaigns);
      let q;

      let currentSubValueSort = subValueSort;
      const fieldsWithSubValueSort = [SORTABLE_COLUMNS.STATUS];
      // Metric sortable keys (used to build composite cursors value+id)
      const metricSortableKeys = [
        SORTABLE_COLUMNS.IMPRESSIONS,
        SORTABLE_COLUMNS.CLICKS,
        SORTABLE_COLUMNS.CTR,
        SORTABLE_COLUMNS.SPEND,
        SORTABLE_COLUMNS.CPM,
        SORTABLE_COLUMNS.CPC,
        SORTABLE_COLUMNS.RESULTS,
        SORTABLE_COLUMNS.CPA,
        SORTABLE_COLUMNS.FREQUENCY,
      ];

      const getCursorIndex = (ids) =>
        ids[ids.length - 1] ? ids.length - 1 : 0;

      // Builder: switch returns the full query configuration for the given sort
      const buildSortQuery = ({ dbRef, sortConfig, lastKey, limit }) => {
        switch (sortConfig.key) {
          case SORTABLE_COLUMNS.CREATION_TIME: {
            if (sortConfig.direction === SORT_DIRECTION.ASC) {
              const orderByField = "creationTime";
              const base = query(dbRef, orderByChild(orderByField));
              const qBuilt = lastKey
                ? query(base, startAfter(lastKey), limitToFirst(limit))
                : query(base, limitToFirst(limit));
              const lastKeyField = "creationTime";
              return { q: qBuilt, lastKeyField };
            }

            // DESC (default) using negative field to invert order naturally
            const orderByField = "creationTimeNegative";
            const base = query(dbRef, orderByChild(orderByField));
            const qBuilt = lastKey
              ? query(base, startAfter(lastKey), limitToFirst(limit))
              : query(base, limitToFirst(limit));
            const lastKeyField = "creationTimeNegative";
            return { q: qBuilt, lastKeyField };
          }

          case SORTABLE_COLUMNS.STATUS: {
            const statesOrdered = Object.keys(STATUS_SORT_ORDER);

            if (!currentSubValueSort) {
              currentSubValueSort =
                sortConfig.direction === SORT_DIRECTION.DESC
                  ? statesOrdered[0]
                  : statesOrdered[statesOrdered.length - 1];
              onChangeSubValueSort(currentSubValueSort);
            }
            const orderByField = "states/general";
            const base = query(dbRef, orderByChild(orderByField));
            const qBuilt = lastKey
              ? query(
                  base,
                  startAt(currentSubValueSort, lastKey),
                  endAt(currentSubValueSort),
                  limitToFirst(limit + 1)
                )
              : query(
                  base,
                  startAt(currentSubValueSort),
                  endAt(currentSubValueSort),
                  limitToFirst(limit)
                );
            const lastKeyField = "states/general";
            const getNextSubValueSort = () => {
              // If ASC, get the next value after currentSubValueSort in statesOrdered; if DESC, get the previous value
              let newValue;
              const currentIndex = statesOrdered.indexOf(currentSubValueSort);
              if (sortConfig.direction === SORT_DIRECTION.DESC) {
                newValue =
                  currentIndex < statesOrdered.length - 1
                    ? statesOrdered[currentIndex + 1]
                    : null; // null if already at the end
              } else {
                newValue =
                  currentIndex > 0 ? statesOrdered[currentIndex - 1] : null; // null if already at the start
              }
              return newValue;
            };

            return {
              q: qBuilt,
              lastKeyField,
              getNextSubValueSort,
            };
          }

          case SORTABLE_COLUMNS.IMPRESSIONS:
          case SORTABLE_COLUMNS.CLICKS:
          case SORTABLE_COLUMNS.CTR:
          case SORTABLE_COLUMNS.SPEND:
          case SORTABLE_COLUMNS.CPM:
          case SORTABLE_COLUMNS.CPC:
          case SORTABLE_COLUMNS.RESULTS:
          case SORTABLE_COLUMNS.CPA:
          case SORTABLE_COLUMNS.FREQUENCY: {
            if (sortConfig.direction === SORT_DIRECTION.ASC) {
              const orderByField = `statistics/total/${sortConfig.key}`;
              const base = query(dbRef, orderByChild(orderByField));
              // Composite cursor (value + id) when available to break ties
              let qBuilt;
              if (
                lastKey &&
                typeof lastKey === "object" &&
                lastKey.value !== undefined &&
                lastKey.id
              ) {
                qBuilt = query(
                  base,
                  startAfter(lastKey.value, lastKey.id),
                  limitToFirst(limit)
                );
              } else if (lastKey) {
                qBuilt = query(base, startAfter(lastKey), limitToFirst(limit));
              } else {
                qBuilt = query(base, startAt(0), limitToFirst(limit));
              }
              const lastKeyField = `statistics/total/${sortConfig.key}`;
              return {
                q: qBuilt,
                lastKeyField,
              };
            }

            // DESC (default) using negative field to invert order naturally
            const orderByField = `statistics/total/${sortConfig.key}Negative`;
            const base = query(dbRef, orderByChild(orderByField));
            let qBuilt;
            if (
              lastKey &&
              typeof lastKey === "object" &&
              lastKey.value !== undefined &&
              lastKey.id
            ) {
              qBuilt = query(
                base,
                startAfter(lastKey.value, lastKey.id),
                limitToFirst(limit)
              );
            } else if (lastKey) {
              qBuilt = query(base, startAfter(lastKey), limitToFirst(limit));
            } else {
              qBuilt = query(
                base,
                startAt(-9999999999),
                endAt(0),
                limitToFirst(limit)
              );
            }
            const lastKeyField = `statistics/total/${sortConfig.key}Negative`;
            return {
              q: qBuilt,
              lastKeyField,
            };
          }

          // Extend here with more cases (e.g., status, spend, etc.) returning { q, lastKeyField }
          default: {
            // Fallback to DESC by creation time
            const orderByField = "creationTimeNegative";
            const base = query(dbRef, orderByChild(orderByField));
            const qBuilt = lastKey
              ? query(base, startAfter(lastKey), limitToFirst(limit))
              : query(base, limitToFirst(limit));
            const lastKeyField = "creationTimeNegative";
            return { q: qBuilt, lastKeyField };
          }
        }
      };

      const {
        q: builtQuery,
        lastKeyField,
        getNextSubValueSort = () => {},
      } = buildSortQuery({
        dbRef,
        sortConfig,
        lastKey,
        limit,
      });

      q = builtQuery;
      const snapshot = await get(q);
      // Build ordered data and ids using snapshot.forEach to preserve query order
      let data = {};
      let campaignIDs = [];
      if (snapshot.exists()) {
        snapshot.forEach((child) => {
          campaignIDs.push(child.key);
          data[child.key] = child.val();
        });
      }
      // For fields with subValueSort (like STATUS), filter duplicate if paginating
      if (
        fieldsWithSubValueSort.includes(sortConfig.key) &&
        lastKey &&
        campaignIDs.length > 0
      ) {
        const lastKeyId =
          typeof lastKey === "object" && lastKey?.id ? lastKey.id : lastKey;
        // Remove the first item if it's the same as lastKey (duplicate from startAt/startAfter)
        if (campaignIDs[0] === lastKeyId) {
          campaignIDs = campaignIDs.slice(1);
          // Remove the duplicate from data as well
          delete data[lastKeyId];
        }
      }

      if (
        (_.isEmpty(data) || campaignIDs.length < 10) &&
        fieldsWithSubValueSort.includes(sortConfig.key)
      ) {
        const nextSubValueSort = getNextSubValueSort();
        if (nextSubValueSort) {
          onChangeSubValueSort(nextSubValueSort);
          return dispatch(
            startGetAdsCampaigns({
              lastKey: null,
              onChangeLastKey,
              onLoadLastKey,
              limit,
              sortConfig,
              subValueSort: nextSubValueSort,
              onChangeSubValueSort,
              isNewSubValueSort: true,
              restCampaigns: { ...data, ...restCampaigns },
            })
          );
        }
      }

      if (_.isEmpty(data)) {
        lastKey || isNewSubValueSort
          ? dispatch(getNextAdsCampaigns({ ...restCampaigns }))
          : dispatch(getAdsCampaigns({ ...restCampaigns }));

        onLoadLastKey();

        return !_.isEmpty(restCampaigns) ? restCampaigns : false;
      }

      // Compute cursor index using strategy provided by the builder
      const index = getCursorIndex(campaignIDs);
      let finalKeyField = lastKeyField.includes("/")
        ? lastKeyField.split("/").join(".")
        : lastKeyField;
      const isMetricKey = metricSortableKeys.includes(sortConfig.key);
      let newLastKey;
      if (isMetricKey) {
        const id = campaignIDs[index];
        const value = _.get(data, `${id}.${finalKeyField}`);
        newLastKey = { id, value };
      } else if (fieldsWithSubValueSort.includes(sortConfig.key)) {
        newLastKey = campaignIDs[index];
      } else {
        newLastKey = _.get(data, `${campaignIDs[index]}.${finalKeyField}`);
      }
      onChangeLastKey(newLastKey);

      lastKey || isNewSubValueSort
        ? dispatch(getNextAdsCampaigns({ ...data, ...restCampaigns }))
        : dispatch(getAdsCampaigns({ ...data, ...restCampaigns }));

      return { ...data, ...restCampaigns };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdsCampaigns",
          context: { lastKey: Boolean(lastKey), limit },
          tags: { action: "getAdsCampaigns" },
          level: "error",
        })
      );
      return false;
    }
  };

export const startUpdateAdsCampaignsStatusAndStatisticsFromPlatforms =
  ({ campaignIDs }) =>
  async (dispatch) => {
    try {
      const { userID: userIDCalling } = dispatch(getPathDatabaseByRole());
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("updateAdsCampaignsStatusAndStatisticsFromPlatforms")}/updateAdsCampaignsStatusAndStatisticsFromPlatforms`,
        data: { campaignIDs },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { userID: userIDEnded } = dispatch(getPathDatabaseByRole());

      if (userIDCalling !== userIDEnded) return false;

      if (data.ok) {
        campaignIDs.forEach((campaignID) => {
          const campaignData = data?.data?.[campaignID];
          if (!campaignData) return;

          dispatch(
            getBasicDataAdsCampaign({
              key: campaignID,
              data: campaignData,
            })
          );
        });

        dispatch(openIntegrationNotFoundModalIfNeeded(data.failedIntegrations));

        return true;
      }

      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName:
            "startUpdateAdsCampaignsStatusAndStatisticsFromPlatforms",
          context: { campaignIDs },
          tags: {
            action: "updateAdsCampaignsStatusAndStatisticsFromPlatforms",
          },
          level: "error",
        })
      );
      return false;
    }
  };
const getBasicDataAdsCampaign = (data) => ({
  type: types.GET_BASIC_DATA_ADS_CAMPAIGN,
  payload: data,
});
export const startUpdateAdsCampaignsInformationFromPlatforms =
  ({ campaignID }) =>
  async (dispatch) => {
    try {
      const { userID: userIDCalling } = dispatch(getPathDatabaseByRole());
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("updateAdsCampaignsInformationFromPlatforms")}/updateAdsCampaignsInformationFromPlatforms`,
        data: { campaignID },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { userID: userIDEnded } = dispatch(getPathDatabaseByRole());

      if (userIDCalling !== userIDEnded) return { ok: false };

      return {
        ok: true,
        data: {
          campaign: getFormattedCampaign({
            data: data.data.campaign,
            format: "load",
          }),
          statistics: data.data.statistics,
        },
      };
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      if (
        errorCode === CAMPAIGN_ALREADY_BEING_CREATED ||
        errorCode === CAMPAIGN_ALREADY_BEING_UPDATED
      ) {
        return { ok: false, code: errorCode };
      }

      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startUpdateAdsCampaignsInformationFromPlatforms",
          context: { campaignID },
          tags: { action: "updateAdsCampaignsInformationFromPlatforms" },
          level: "error",
        })
      );
      return errorFormatted;
    }
  };
/**
 * Per-platform goal on the campaign itself:
 * adsCampaigns/{advertiserID}/{campaignID}/platformsData/{platform}/kpiSettings =
 *   { primaryKpi, targetCpa, targetCpaCurrency, volume }
 * volume is the weekly result target for that KPI.
 * Returned in the shape the modal already edits.
 */
export const startGetCampaignKpiTargets =
  ({ campaignID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !campaignID) {
        return { ok: false, kpiTargets: null, code: "MISSING_PARAMETERS" };
      }

      const snapshot = await get(
        ref(db, `adsCampaigns/${advertiserID}/${campaignID}/platformsData`)
      );
      const platformsData = snapshot.val() || {};
      const platformTargets = {};
      Object.entries(platformsData).forEach(([platform, row]) => {
        const settings = row?.kpiSettings;
        if (!settings || typeof settings !== "object") return;
        platformTargets[platform] = {
          primaryKpi:
            typeof settings.primaryKpi === "string"
              ? settings.primaryKpi
              : null,
          targetCpa: settings.targetCpa ?? null,
          currency: settings.targetCpaCurrency || null,
          volume: settings.volume ?? null,
        };
      });

      return { ok: true, advertiserID, kpiTargets: { platformTargets } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetCampaignKpiTargets",
          context: { campaignID },
        })
      );
      return { ok: false, kpiTargets: null };
    }
  };

/**
 * Writes each platform goal onto platformsData. Platforms omitted from
 * kpiTargets are cleared so the account goal applies again.
 */
export const startUpdateCampaignKpiTargets =
  ({ campaignID, kpiTargets, platforms = [] }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !campaignID) {
        return { ok: false, code: "MISSING_PARAMETERS" };
      }

      const updates = {};
      const platformTargets = kpiTargets?.platformTargets || {};
      platforms.forEach((platform) => {
        const path = `adsCampaigns/${advertiserID}/${campaignID}/platformsData/${platform}/kpiSettings`;
        const row = platformTargets[platform];
        updates[path] = row
          ? {
              primaryKpi: row.primaryKpi,
              targetCpa: row.targetCpa,
              targetCpaCurrency: row.currency,
              ...(row.volume ? { volume: row.volume } : {}),
            }
          : null;
      });

      await update(ref(db), updates);

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUpdateCampaignKpiTargets",
          context: { campaignID },
          tags: { operation: "update_campaign_kpi_targets" },
        })
      );
      return { ok: false, message: error?.message };
    }
  };

export const startSetNewVersionReportFlag =
  ({ campaignID }) =>
  async (dispatch) => {
    try {
      const { pathCampaigns } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(
        db,
        `${pathCampaigns}/${campaignID}/report/isNewVersion`
      );
      await set(dbRef, true);

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSetNewVersionReportFlag",
          capture: "exception",
        })
      );
      console.log(error);
      return false;
    }
  };
export const startGetDetailedCampaignReport =
  ({ campaignID, breakdown, startDateTime, endDateTime, getPreviousPeriod }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const language = getLanguageFormatted(i18next.language);

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getDetailedCampaignReport")}/getDetailedCampaignReport`,
        data: {
          campaignID,
          language,
          breakdown,
          startDateTime: formatDateForStatistics(startDateTime),
          endDateTime: formatDateForStatistics(endDateTime),
          getPreviousPeriod,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const failedIntegrations = data?.data?.failedIntegrations;
      if (Array.isArray(failedIntegrations) && failedIntegrations.length > 0) {
        dispatch(
          openIntegrationNotFoundModalIfNeeded(failedIntegrations, {
            forceShow: true,
          })
        );
      }

      return {
        ok: data.ok,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(
          error,
          false,
          [MISSING_PARAMETERS],
          {
            functionName: "startGetDetailedCampaignReport",
            context: {
              campaignID,
              breakdown,
              startDateTime,
              endDateTime,
              getPreviousPeriod,
            },
            tags: { action: "getDetailedCampaignReport" },
            level: "error",
          },
          {
            forceShowIntegrationNotFound: true,
          }
        )
      );
      return false;
    }
  };

export const startChangeStatusAdsCampaign =
  ({ states, campaignID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("updateAdsCampaignPlatforms")}/updateAdsCampaignPlatforms`,
        data: {
          states,
          campaignID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.ok) {
        dispatch(
          saveAdsCampaign({
            key: campaignID,
            data: {
              states,
            },
          })
        );
      }

      return data.ok;
    } catch (error) {
      console.log(error);
      const errorFormatted = getObjectError(error);
      if (ERRORS_BILLING.includes(errorFormatted.code)) {
        SimpleAlert({
          title: i18next.t(ERROR),
          text: i18next.t(ERROR_DESCRIPTION_GENERIC, {
            message: errorFormatted.message,
            code: errorFormatted.code,
          }),
          icon: ALERT_ICON_TYPE_ERROR,
        });
      } else {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "startChangeStatusAdsCampaign",
            context: { campaignID, states },
            tags: { action: "changeStatusAdsCampaign" },
            level: "error",
          })
        );
      }
      return false;
    }
  };

export const getAdsCampaigns = (data) => ({
  type: types.GET_ADS_CAMPAIGNS_FINISH,
  payload: data,
});
const getNextAdsCampaigns = (data) => ({
  type: types.GET_NEXT_ADS_CAMPAIGNS__FINISH,
  payload: data,
});

const saveAdsCampaign = (data) => ({
  type: types.SAVE_ADS_CAMPAIGN_FINISH,
  payload: data,
});

export const startGetRecommendedSettingsPlatform =
  (campaign) => async (dispatch, getState) => {
    let createdJobID = null;

    try {
      const language = getLanguageFormatted(i18next.language);
      const geoIP = getState().user.geoIP;

      const data = await createCreativeJobAndWaitForResponse({
        dispatch,
        jobType: "generateRecommendedSettingsPlatform",
        payload: {
          ...campaign,
          languageReasoning: language,
          userLocation: geoIP,
        },
        onJobCreated: ({ jobID }) => {
          createdJobID = jobID;
          if (!campaign?.campaignID) return;

          migrateCreativeJobProgress({
            fromKey: campaign.campaignID,
            toKey: jobID,
          });
          dispatch(
            promoteCreativeNotification({
              fromId: campaign.campaignID,
              jobID,
            })
          );
        },
      });

      const campaignGenerated = data?.data?.campaign;

      return {
        ok: true,
        data: getFormattedCampaign({
          data: campaignGenerated,
          format: "load",
        }),
        jobID: createdJobID || data?.jobID,
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, false, [ERROR_GETTING_URL_META_DATA_AND_IMAGES], {
          functionName: "startGetRecommendedSettingsPlatform",
          context: {},
          tags: { action: "getRecommendedSettingsPlatform" },
          level: "error",
          creativeJobType:
            CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform,
        })
      );

      return {
        ...errorFormatted,
        jobID: createdJobID,
      };
    }
  };

export const startCreateAdsCampaign = (campaign) => async (dispatch) => {
  const { userID: advertiserID, agencyID } = dispatch(getPathDatabaseByRole());
  const userID = dispatch(getUserID());
  const email = dispatch(getUserEmail());

  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("createAdsCampaignPlatforms")}/createAdsCampaignPlatforms`,
      data: getFormattedCampaign({ data: campaign, format: "save" }),
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (data.ok) {
      const campaign = data?.data?.campaign;

      dispatch(
        saveAdsCampaign({
          key: campaign?.id,
          data: campaign,
        })
      );

      return {
        ok: true,
        campaign: getFormattedCampaign({
          data: campaign,
          format: "load",
        }),
      };
    }
  } catch (error) {
    console.log(error);
    const errorFormatted = getObjectError(error);
    const campaignID = errorFormatted.data?.id || campaign?.id || null;

    dispatch(
      LaunchError(error, true, [], {
        functionName: "startCreateAdsCampaign",
        context: {
          endpoint: "createAdsCampaignPlatforms",
          userID,
          email,
          advertiserID,
          agencyID,
          campaignID,
        },
        tags: {
          action: "createAdsCampaign",
          endpoint: "createAdsCampaignPlatforms",
          errorType:
            errorFormatted.code === ERROR_PROCESSING_CAMPAIGNS
              ? "processingCampaigns"
              : "crash",
        },
        level: "error",
      })
    );

    if (errorFormatted.code === ERROR_PROCESSING_CAMPAIGNS) {
      const data = errorFormatted.data;
      dispatch(
        saveAdsCampaign({
          key: data?.id,
          data: data?.campaign,
        })
      );
      const returnError = {
        ...errorFormatted,
        ...data,
        campaign: getFormattedCampaign({
          data: data?.campaign,
          format: "load",
        }),
      };

      delete returnError.data;
      return returnError;
    }

    return { ok: false, code: errorFormatted.code };
  }
};

export const startDeleteAdsCampaign = (basicCampaign) => async (dispatch) => {
  try {
    const { userID } = dispatch(getPathDatabaseByRole());
    const campaignID = basicCampaign?.id;
    const completeCampaign = await dispatch(
      startGetAdsCampaign(campaignID, true)
    );
    const campaign = getFormattedCampaign({
      data: completeCampaign,
      format: "save",
    });
    const updates = {};
    updates[`adsCampaigns/${userID}/${campaignID}`] = null;

    if (!_.isEmpty(campaign?.adsGroups)) {
      Object.keys(campaign?.adsGroups || {}).forEach((adGroup) => {
        updates[`adsGroups/${userID}/${adGroup}`] = null;
        Object.keys(campaign?.adsGroups[adGroup]?.ads || {}).forEach((ad) => {
          updates[`ads/${userID}/${ad}`] = null;
        });
      });
    }

    if (!_.isEmpty(campaign?.assetsGroups)) {
      Object.keys(campaign?.assetsGroups || {}).forEach((assetGroup) => {
        updates[`assetsGroups/${userID}/${assetGroup}`] = null;
      });
    }
    updates[`adsCampaignsArchived/${userID}/${campaignID}`] = campaign;
    await update(ref(db), updates);
    dispatch(deleteCampaign({ campaignID }));
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startDeleteAdsCampaign",
        context: { campaignID: basicCampaign?.id },
        tags: { action: "deleteAdsCampaign" },
        level: "error",
      })
    );

    return { ok: false };
  }
};

const deleteCampaign = (payload) => ({
  type: types.DELETE_ADS_CAMPAIGN_FINISH,
  payload,
});

export const generateAdsCampaignDescription =
  (campaign) => async (dispatch, getState) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("generateAdsCampaignDescription")}/generateAdsCampaignDescription`,
        data: campaign,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (result.ok) {
        return result?.data;
      }

      const paymentMode =
        getState()?.payments?.byokConfigurationPublic?.defaultPaymentMode ||
        "credits";

      SimpleAlert({
        title: i18next.t(ERROR),
        text: resolveChatAgentErrorMessage({
          errorOrResponse: result,
          paymentMode,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "generateAdsCampaignDescription",
          context: {},
          tags: { action: "generateAdsCampaignDescription" },
          level: "error",
        })
      );

      const paymentMode =
        getState()?.payments?.byokConfigurationPublic?.defaultPaymentMode ||
        "credits";

      SimpleAlert({
        title: i18next.t(ERROR),
        text: resolveChatAgentErrorMessage({
          errorOrResponse: error,
          paymentMode,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    }
  };

export const regenerateAdTextsFromCreative = (payload) => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());
    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("regenerateAdTextsFromCreative")}/regenerateAdTextsFromCreative`,
      data: payload,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (result?.ok) {
      const copyPayload = result?.data;
      if (
        copyPayload &&
        typeof copyPayload === "object" &&
        (Array.isArray(copyPayload.titles) ||
          Array.isArray(copyPayload.headlines) ||
          Array.isArray(copyPayload.descriptions))
      ) {
        return copyPayload;
      }
      if (
        copyPayload?.data &&
        typeof copyPayload.data === "object" &&
        (Array.isArray(copyPayload.data.titles) ||
          Array.isArray(copyPayload.data.headlines) ||
          Array.isArray(copyPayload.data.descriptions))
      ) {
        return copyPayload.data;
      }
      return copyPayload || null;
    }

    return null;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "regenerateAdTextsFromCreative",
        context: {},
        tags: { action: "regenerateAdTextsFromCreative" },
        level: "error",
      })
    );
    return null;
  }
};

export const generateSocialDescriptionFromUrl =
  ({ url: mediaUrl, language, description = "" }) =>
  async (dispatch, getState) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("generateSocialDescriptionFromUrl")}/generateSocialDescriptionFromUrl`,
        data: { url: mediaUrl, language, description },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (result.ok) {
        return result?.data?.socialDescription;
      }

      const paymentMode =
        getState()?.payments?.byokConfigurationPublic?.defaultPaymentMode ||
        "credits";

      SimpleAlert({
        title: i18next.t(ERROR),
        text: resolveChatAgentErrorMessage({
          errorOrResponse: result,
          paymentMode,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "generateSocialDescriptionFromUrl",
          context: {},
          tags: { action: "generateSocialDescriptionFromUrl" },
          level: "error",
        })
      );

      const paymentMode =
        getState()?.payments?.byokConfigurationPublic?.defaultPaymentMode ||
        "credits";

      SimpleAlert({
        title: i18next.t(ERROR),
        text: resolveChatAgentErrorMessage({
          errorOrResponse: error,
          paymentMode,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    }
  };

export const startGetAdsCampaignTrackingIDs =
  ({ platform, integrationID, adAccountID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getAdsCampaignTrackingIDs")}/getAdsCampaignTrackingIDs`,
        data: { platform, integrationID, adAccountID },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.ok) {
        return data.data;
      }

      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [NO_PIXELS_FOUND], {
          functionName: "startGetAdsCampaignTrackingIDs",
          context: { platform, integrationID, adAccountID },
          tags: { action: "getAdsCampaignTrackingIDs" },
          level: "error",
        })
      );
      return false;
    }
  };

const fetchFormattedAdsCampaign = async ({ dispatch, campaignID }) => {
  const paths = dispatch(getPathDatabaseByRole());
  const { pathCampaigns, pathAdSets, pathAssetsGroups, pathAds } = paths;

  const dbRef = ref(db, `${pathCampaigns}/${campaignID}`);
  const snapshot = await get(dbRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.val();
  const adGroupsQueries = [];
  const assetsGroupsQueries = [];
  const adsQueries = [];

  const adsGroups = data?.adsGroups;
  const assetsGroups = data?.assetsGroups;
  if (!_.isEmpty(assetsGroups)) {
    Object.keys(assetsGroups).forEach((key) => {
      assetsGroupsQueries.push(get(ref(db, `${pathAssetsGroups}/${key}`)));
    });
    data.assetsGroups = {};
  }

  const assetsGroupsSnapshots = await Promise.allSettled(assetsGroupsQueries);
  assetsGroupsSnapshots.forEach((assetsGroupSnapshot) => {
    if (assetsGroupSnapshot.status !== "fulfilled") return;

    const dataAssetsGroup = assetsGroupSnapshot.value.val();
    if (!dataAssetsGroup) return;

    const assetGroupID = dataAssetsGroup?.id;
    data.assetsGroups[assetGroupID] = dataAssetsGroup;
  });

  if (!adsGroups) {
    return getFormattedCampaign({ data, format: "load" });
  }

  Object.keys(adsGroups).forEach((key) => {
    adGroupsQueries.push(get(ref(db, `${pathAdSets}/${key}`)));
  });

  data.adsGroups = {};

  const adGroupsSnapshots = await Promise.allSettled(adGroupsQueries);

  adGroupsSnapshots.forEach((adGroupSnapshot) => {
    if (adGroupSnapshot.status !== "fulfilled") return;

    const dataAdGroup = adGroupSnapshot.value.val();
    if (!dataAdGroup) return;

    const adGroupID = dataAdGroup?.id;

    data.adsGroups[adGroupID] = dataAdGroup;

    const ads = dataAdGroup?.ads;
    if (!ads) return;

    Object.keys(ads).forEach((key) => {
      adsQueries.push(get(ref(db, `${pathAds}/${key}`)));
    });

    data.adsGroups[adGroupID].ads = {};
  });

  if (adsQueries.length === 0) {
    return getFormattedCampaign({ data, format: "load" });
  }

  const adsSnapshots = await Promise.allSettled(adsQueries);

  adsSnapshots.forEach((adSnapshot) => {
    if (adSnapshot.status !== "fulfilled") return;

    const dataAd = adSnapshot.value.val();
    if (!dataAd) return;

    const adID = dataAd.id;
    const adGroupID = dataAd.adGroupID;

    if (!adID || !adGroupID) return;
    if (!data?.adsGroups?.[adGroupID]?.ads) return;

    data.adsGroups[adGroupID].ads[adID] = dataAd;
  });

  return getFormattedCampaign({ data, format: "load" });
};

const storeLoadedAdsCampaign = ({
  dispatch,
  campaignID,
  dataFormatted,
  isNext,
}) => {
  dispatch(
    isNext
      ? getNextAdsCampaigns({
          [campaignID]: dataFormatted,
        })
      : getAdsCampaigns({
          [campaignID]: dataFormatted,
        })
  );
};

export const startGetAdsCampaign =
  (campaignID, isNext = false) =>
  async (dispatch) => {
    try {
      const dataFormatted = await fetchFormattedAdsCampaign({
        dispatch,
        campaignID,
      });

      if (!dataFormatted) {
        return false;
      }

      storeLoadedAdsCampaign({
        dispatch,
        campaignID,
        dataFormatted,
        isNext,
      });

      return dataFormatted;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdsCampaign",
          context: { campaignID, isNext },
          tags: { action: "getAdsCampaign" },
          level: "error",
        })
      );
      return false;
    }
  };

export const startGetAdsCampaignTree =
  ({ campaignID }) =>
  async (dispatch) => {
    try {
      const dataFormatted = await fetchFormattedAdsCampaign({
        dispatch,
        campaignID,
      });

      if (!dataFormatted) {
        dispatch(
          LaunchError(new Error("Campaign tree not found"), false, [], {
            functionName: "startGetAdsCampaignTree",
            context: { campaignID },
            tags: { action: "getAdsCampaignTree" },
            level: "error",
          })
        );
        return { ok: false };
      }

      return {
        ok: true,
        data: dataFormatted,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdsCampaignTree",
          context: { campaignID },
          tags: { action: "getAdsCampaignTree" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startListenerAdsCampaignActionStatus =
  ({
    campaignID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `adsCampaignsActionStatus/${userID}/${campaignID}`);
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        const data = snapshot.val();
        onUpdatedDataCallback(data);
      });
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerAdsCampaignActionStatus",
          context: { campaignID },
          tags: { action: "listenerAdsCampaignActionStatus" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerAdsCampaignActionStatus =
  (listenerRef) => async () => {
    if (!listenerRef) {
      return;
    }

    await off(listenerRef);
  };

export const startSaveAdsCampaignDraft =
  ({ campaign, campaignID }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      const actionLock = (
        await get(ref(db, `adsCampaignsActionStatus/${userID}/${campaignID}`))
      ).val();
      if (actionLock?.status && Number(actionLock?.expiresAt) > Date.now()) {
        SimpleAlert({
          title: i18next.t(ERROR),
          text: i18next.t(
            actionLock.status === ADS_CAMPAIGN_ACTION_STATUS.creating
              ? CAMPAIGN_ALREADY_BEING_CREATED
              : CAMPAIGN_ALREADY_BEING_UPDATED
          ),
          icon: ALERT_ICON_TYPE_ERROR,
        });
        return false;
      }

      const campaignFormatted = replaceUndefinedWithNull(
        getFormattedCampaign({
          data: campaign,
          format: "save",
        })
      );
      const updates = {};

      const adsGroups = {};
      const assetsGroups = {};

      Object.keys(campaignFormatted?.adsGroups || {}).forEach((key) => {
        adsGroups[key] = true;
        const ads = {};

        Object.keys(campaignFormatted?.adsGroups[key]?.ads).forEach((keyAd) => {
          ads[keyAd] = true;

          updates[`ads/${userID}/${keyAd}`] = {
            ...campaignFormatted?.adsGroups[key]?.ads[keyAd],
            isCarousel:
              campaignFormatted?.adsGroups?.[key]?.ads?.[keyAd]?.isCarousel ??
              false,
          };
        });

        updates[`adsGroups/${userID}/${key}`] = {
          ...campaignFormatted?.adsGroups[key],
          ads,
        };
      });

      Object.keys(campaignFormatted?.assetsGroups || {}).forEach((key) => {
        assetsGroups[key] = true;
        updates[`assetsGroups/${userID}/${key}`] = {
          ...campaignFormatted?.assetsGroups[key],
        };
      });

      updates[`adsCampaigns/${userID}/${campaignID}`] = {
        ...campaignFormatted,
        actionStatus: null,
        adsGroups,
        assetsGroups,
      };

      const dbRef = ref(db);
      await update(dbRef, updates);

      dispatch(
        saveAdsCampaign({
          key: campaignID,
          data: campaignFormatted,
        })
      );

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveAdsCampaignDraft",
          context: { campaignID },
          tags: { action: "saveAdsCampaignDraft" },
          level: "error",
        })
      );
      return false;
    }
  };

export const startDuplicateAdsCampaign =
  ({ campaign }) =>
  async (dispatch, getState) => {
    try {
      const { userID, agencyID } = dispatch(getPathDatabaseByRole());
      const updates = {};
      const campaignToSave = getFormattedCampaign({
        data: campaign,
        format: "save",
      });
      const newCampaign = _.cloneDeep(replaceUndefinedWithNull(campaignToSave));
      const newCampaignID = push(ref(db, `adsCampaigns/${userID}`)).key;
      const statesKeys = Object.keys(newCampaign.states);
      const newStates = {};
      statesKeys.forEach((key) => {
        newStates[key] = ADS_CAMPAIGN_STATUS_DRAFT;
      });

      newCampaign.id = newCampaignID;
      newCampaign.name = `${newCampaign.name} - ${i18next.t(COPY)}`;
      newCampaign.states = newStates;
      newCampaign.status = ADS_CAMPAIGN_STATUS_DRAFT;
      newCampaign.creationTime = new Date().getTime();
      newCampaign.creationTimeNegative = -newCampaign.creationTime;
      newCampaign.createdBy = dispatch(getUserID());
      delete newCampaign.firstActiveTime;
      delete newCampaign.lastActiveTime;
      delete newCampaign.isFirstActive;
      delete newCampaign.platformIDs;
      delete newCampaign.report;
      delete newCampaign.statistics;
      delete newCampaign.statistics;
      delete newCampaign.generatedPlatforms;

      // Clear platform alerts so the duplicated campaign does not inherit previous errors
      if (newCampaign?.platformsData) {
        Object.keys(newCampaign.platformsData || {}).forEach((platformKey) => {
          if (newCampaign.platformsData[platformKey]?.alerts) {
            newCampaign.platformsData[platformKey].alerts = [];
          }
          // Update name in platformsData for each platform
          if (newCampaign.platformsData[platformKey]?.name) {
            newCampaign.platformsData[platformKey].name =
              `${newCampaign.platformsData[platformKey].name} - ${i18next.t(COPY)}`;
          }
        });
      }

      if (newCampaign?.platformsData?.[GOOGLE]) {
        delete newCampaign.platformsData[GOOGLE].budgetID;
        delete newCampaign.platformsData[GOOGLE].mediaIDs;
        delete newCampaign.platformsData[GOOGLE].logoID;
        delete newCampaign.platformsData[GOOGLE].businessNameID;
        delete newCampaign.platformsData[GOOGLE].locationsIDs;
        delete newCampaign.platformsData[GOOGLE].siteLinksIDs;
        delete newCampaign.platformsData[GOOGLE].callOutsIDs;
        delete newCampaign.platformsData[GOOGLE].brandGuidelinesEnabled;

        if (!newCampaign?.platformsData?.[GOOGLE]?.networkSettings) {
          newCampaign.platformsData[GOOGLE].networkSettings = {
            targetContentNetwork: false,
            targetGoogleSearch: true,
            targetSearchNetwork: false,
          };
        }
      }
      if (newCampaign?.platformsData?.[LINKEDIN]) {
        delete newCampaign.platformsData[LINKEDIN].campaignGroupID;
      }

      const adsGroups = newCampaign.adsGroups;
      const assetsGroups = newCampaign.assetsGroups;
      const newAdsGroupsIDs = {};
      const newAssetsGroupsIDs = {};
      Object.values(adsGroups).forEach((adGroup) => {
        const newAdGroupID = push(ref(db, `adsGroups/${userID}`)).key;
        newAdsGroupsIDs[newAdGroupID] = true;

        adGroup.id = newAdGroupID;
        adGroup.campaignID = newCampaignID;
        adGroup.creationTime = new Date().getTime();
        adGroup.creationTimeNegative = -adGroup.creationTime;
        adGroup.status = ADS_CAMPAIGN_STATUS_DRAFT;
        adGroup.isIncompatible = false;
        delete adGroup.idPlatform;
        delete adGroup.estimationID;

        if (adGroup.platform === GOOGLE) {
          delete adGroup.keywordsIDs;
          delete adGroup.keywordsNegativeIDs;
        }

        const ads = adGroup.ads;
        const newAdsIDs = {};

        Object.values(ads).forEach((ad) => {
          const newAdID = push(ref(db, `ads/${userID}`)).key;
          newAdsIDs[newAdID] = true;

          ad.id = newAdID;
          ad.adGroupID = newAdGroupID;
          ad.campaignID = newCampaignID;
          ad.creationTime = new Date().getTime();
          ad.creationTimeNegative = -ad.creationTime;
          ad.status = ADS_CAMPAIGN_STATUS_DRAFT;
          ad.isIncompatible = false;
          delete ad.idPlatform;
          delete ad.creativeID;
          delete ad.mediaIDs;

          updates[`ads/${userID}/${newAdID}`] = ad;
        });

        adGroup.ads = newAdsIDs;
        updates[`adsGroups/${userID}/${newAdGroupID}`] = adGroup;
      });

      Object.values(assetsGroups).forEach((assetGroup) => {
        const newAssetsGroupID = push(ref(db, `assetsGroups/${userID}`)).key;
        newAssetsGroupsIDs[newAssetsGroupID] = true;

        assetGroup.id = newAssetsGroupID;
        assetGroup.campaignID = newCampaignID;
        assetGroup.creationTime = new Date().getTime();
        assetGroup.creationTimeNegative = -assetGroup.creationTime;
        assetGroup.status = ADS_CAMPAIGN_STATUS_DRAFT;
        assetGroup.isIncompatible = false;
        delete assetGroup.idPlatform;
        delete assetGroup.titlesIDs;
        delete assetGroup.longHeadlinesIDs;
        delete assetGroup.descriptionsIDs;
        delete assetGroup.businessNameID;
        delete assetGroup.imageIDs;
        delete assetGroup.logoIDs;
        delete assetGroup.videoIDs;
        delete assetGroup.searchThemesIDs;
        delete assetGroup.audienceID;

        updates[`assetsGroups/${userID}/${newAssetsGroupID}`] = assetGroup;
      });

      newCampaign.adsGroups = newAdsGroupsIDs;
      newCampaign.assetsGroups = newAssetsGroupsIDs;
      updates[`adsCampaigns/${userID}/${newCampaignID}`] = newCampaign;
      updates[`adsCampaignsIDsByAgency/${agencyID}/${newCampaignID}`] = {
        creationTime: newCampaign.creationTime,
      };

      const typeOfPlans = getState()?.payments?.typeOfPlans;

      if (typeOfPlans === PLAN_TYPE_CREDITS) {
        const responseVerify = await dispatch(
          startVerifyAndSetAdsUsage({
            actionID: ADS_ACTION_TYPES.GENERATE_CAMPAIGN,
          })
        );

        if (!responseVerify.ok) {
          return {
            ok: false,
            error: responseVerify.error,
          };
        }
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          campaignID: newCampaignID,
        },
      };
    } catch (error) {
      console.log(error);
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDuplicateAdsCampaign",
          context: {},
          tags: { action: "duplicateAdsCampaign" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startUpdateCampaignEntityStatus =
  ({
    campaignID,
    platform,
    entity,
    entityID,
    status,
    optimisticSwitch,
    showErrorAlert,
  }) =>
  async (dispatch) => {
    try {
      if (ADS_CAMPAIGNS_DEMO.includes(campaignID)) {
        await waitDelay(5000);

        return {
          ok: true,
        };
      }

      if (entity === ADS_CAMPAIGN_ENTITIES.ALL_CAMPAIGNS) {
        dispatch(setAdsCampaignStatusOverride({ campaignID, status }));
      }

      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("updateCampaignEntityStatus")}/updateCampaignEntityStatus`,
        data: {
          campaignID,
          platform,
          entity,
          entityID,
          status,
          optimisticSwitch,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return { ok: true, reportCleared: Boolean(data?.reportCleared) };
    } catch (error) {
      console.log(error);
      if (entity === ADS_CAMPAIGN_ENTITIES.ALL_CAMPAIGNS) {
        dispatch(removeAdsCampaignStatusOverrides([campaignID]));
      }
      dispatch(
        LaunchError(
          error,
          showErrorAlert ?? !optimisticSwitch?.operationId,
          [],
          {
            functionName: "startUpdateCampaignEntityStatus",
            context: { campaignID, platform, entity, entityID, status },
            tags: { action: "updateCampaignEntityStatus" },
            level: "error",
          },
          {
            forceShowIntegrationNotFound: status === ADS_CAMPAIGN_STATUS_ACTIVE,
          }
        )
      );
      return {
        ok: false,
        error,
        operationId: optimisticSwitch?.operationId,
      };
    }
  };
export const startSaveAdsCampaign =
  ({ campaign, campaignID, from = "update" }) =>
  async (dispatch) => {
    const { userID: advertiserID, agencyID } = dispatch(
      getPathDatabaseByRole()
    );
    const userID = dispatch(getUserID());
    const email = dispatch(getUserEmail());
    try {
      await dispatch(startSaveDraftCampaign({ campaign, campaignID }));

      await dispatch(
        setPublishingCampaignState({
          currentCampaign: campaign,
        })
      );

      if (ADS_CAMPAIGNS_DEMO.includes(campaignID) && from === "optimization") {
        await waitDelay(5000);

        return {
          ok: true,
          campaign,
        };
      }

      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("updateAdsCampaignPlatforms")}/updateAdsCampaignPlatforms`,
        data: getFormattedCampaign({
          data: {
            ...campaign,
            campaignID,
          },
          format: "save",
        }),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.ok) {
        await dispatch(startDeleteDraftCampaign({ campaignID }));

        const campaign = data?.data?.campaign;

        dispatch(
          saveAdsCampaign({
            key: campaignID,
            data: campaign,
          })
        );
        return {
          ok: true,
          campaign: getFormattedCampaign({ data: campaign, format: "load" }),
        };
      }

      return { ok: false };
    } catch (error) {
      console.log(error);
      const errorFormatted = getObjectError(error);
      const campaignID = errorFormatted.data?.id || campaign?.id || null;

      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveAdsCampaign",
          context: {
            endpoint: "updateAdsCampaignPlatforms",
            userID,
            email,
            advertiserID,
            agencyID,
            campaignID,
          },
          tags: {
            action: "updateAdsCampaign",
            endpoint: "updateAdsCampaignPlatforms",
            errorType:
              errorFormatted.code === ERROR_PROCESSING_CAMPAIGNS
                ? "processingCampaigns"
                : "crash",
          },
          level: "error",
        })
      );

      if (errorFormatted.code === ERROR_PROCESSING_CAMPAIGNS) {
        const data = errorFormatted.data;
        dispatch(
          saveAdsCampaign({
            key: data?.id,
            data: data?.campaign,
          })
        );
        const returnError = {
          ...errorFormatted,
          ...data,
          campaign: getFormattedCampaign({
            data: data?.campaign,
            format: "load",
          }),
        };

        delete returnError.data;
        return returnError;
      }

      return { ok: false, code: errorFormatted.code };
    }
  };
export const startVerifyAndSetAdsUsage =
  ({ actionID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("verifyAndSetAdsUsage")}/verifyAndSetAdsUsage`,
        data: {
          actionID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return { ok: data.ok };
    } catch (error) {
      console.log(error);
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startVerifyAndSetAdsUsage",
          context: { actionID },
          tags: { action: "verifyAndSetAdsUsage" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
export const startUploadContentsToPlatforms =
  ({ mediaUrlsByPlatform, campaignID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("uploadContentsToPlatforms")}/uploadContentsToPlatforms`,
        data: {
          mediaUrlsByPlatform,
          campaignID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data.ok;
    } catch (error) {
      console.log(error);
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUploadContentsToPlatforms",
          context: { campaignID },
          tags: { action: "uploadContentsToPlatforms" },
          level: "error",
        })
      );
      return false;
    }
  };

export const startGetAdsCampaignSegmentation =
  ({ keyWord, type, platform, objective, integrationID, adAccountID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const language = getLanguageFormatted(i18next.language);

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getAdsCampaignSegmentation")}/getAdsCampaignSegmentation`,
        data: {
          keyWords: [keyWord],
          type,
          platform,
          objective,
          language,
          integrationID,
          adAccountID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.ok) {
        return data.data;
      }

      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdsCampaignSegmentation",
          context: { keyWord, type, platform, objective },
          tags: { action: "getAdsCampaignSegmentation" },
          level: "error",
        })
      );
      return false;
    }
  };

export const startGetInstantForm =
  ({
    lastKey,
    adAccountID,
    integrationID,
    pageID,
    onChangeLastKey = () => {},
    onChangeLoadLastItem = () => {},
    platform,
    instantFormID,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getInstantFormsAdsCampaigns")}/getInstantFormsAdsCampaigns`,
        data: {
          lastKey,
          integrationID,
          adAccountID,
          pageID,
          platform,
          instantFormID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const response = data.data;

      if (!response?.hasMore) {
        onChangeLoadLastItem(true);
      }

      onChangeLastKey(response?.lastKey);

      return {
        ok: true,
        data: response?.instantForms || [],
      };
    } catch (error) {
      const formatted = await dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetInstantForm",
          context: {},
          tags: { action: "getInstantFormsAdsCampaigns" },
          level: "error",
        })
      );
      return formatted || { ok: false };
    }
  };

export const startGetTikTokInstantPageEditorToken =
  ({ integrationID, adAccountID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getTikTokInstantPageEditorToken")}/getTikTokInstantPageEditorToken`,
        data: {
          integrationID,
          adAccountID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    } catch (error) {
      return dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetTikTokInstantPageEditorToken",
          context: { integrationID, adAccountID },
          tags: { action: "getTikTokInstantPageEditorToken" },
          level: "error",
        })
      );
    }
  };

const MAX_LEAD_FORM_LOOKUP_PAGES = 20;

const leadFormByIDCache = new Map();

const getLeadFormByIDCacheKey = ({
  platform,
  adAccountID,
  pageID,
  leadFormID,
}) => `${platform}:${adAccountID || pageID}:${leadFormID}`;

// Looks up a lead form by id paging the platform list (LinkedIn, Meta).
export const startGetLeadFormByID =
  ({
    integrationID,
    adAccountID,
    pageID,
    leadFormID,
    platform = LINKEDIN,
    shouldContinue = () => true,
  }) =>
  async (dispatch) => {
    if (!leadFormID) {
      return {
        ok: false,
        code: "notFound",
      };
    }

    const cacheKey = getLeadFormByIDCacheKey({
      platform,
      adAccountID,
      pageID,
      leadFormID,
    });
    const cached = leadFormByIDCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      let lastKey;
      let hasMore = true;

      const onChangeLastKey = (nextLastKey) => {
        lastKey = nextLastKey;
      };

      const onChangeLoadLastItem = (isLastItem) => {
        if (isLastItem) {
          hasMore = false;
        }
      };

      for (
        let page = 0;
        page < MAX_LEAD_FORM_LOOKUP_PAGES && hasMore;
        page += 1
      ) {
        if (!shouldContinue()) {
          return {
            ok: false,
            code: "aborted",
          };
        }

        const previousLastKey = lastKey;

        const response = await dispatch(
          startGetInstantForm({
            integrationID,
            adAccountID,
            pageID,
            platform,
            lastKey,
            instantFormID: leadFormID,
            onChangeLastKey,
            onChangeLoadLastItem,
          })
        );

        if (!response?.ok) {
          return {
            ok: false,
            message: response?.message,
            code: response?.code,
          };
        }

        const instantForms = Array.isArray(response.data) ? response.data : [];
        const leadForm = instantForms.find((item) =>
          isSameLeadFormID({ a: item?.id, b: leadFormID })
        );

        if (leadForm) {
          const result = {
            ok: true,
            data: leadForm,
          };
          leadFormByIDCache.set(cacheKey, result);
          return result;
        }

        // hasMore without a new cursor would request the same page again.
        const cursorAdvanced =
          lastKey != null && lastKey !== "" && lastKey !== previousLastKey;
        if (hasMore && !cursorAdvanced) {
          break;
        }
      }

      return {
        ok: false,
        code: "notFound",
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetLeadFormByID",
          context: { integrationID, adAccountID, pageID, leadFormID, platform },
          tags: { action: "getLeadFormByID" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startCreateLeadForm =
  ({
    integrationID,
    adAccountID,
    platform,
    name,
    language,
    headline,
    description,
    profileFields,
    customQuestions,
    privacyPolicyUrl,
    legalDisclaimer,
    thankYouMessage,
    callToActionLabel,
    landingPageUrl,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createLeadFormAdsCampaigns")}/createLeadFormAdsCampaigns`,
        data: {
          integrationID,
          adAccountID,
          platform,
          name,
          language,
          headline,
          description,
          profileFields,
          customQuestions,
          privacyPolicyUrl,
          legalDisclaimer,
          thankYouMessage,
          callToActionLabel,
          landingPageUrl,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        reportCreateLeadFormAdsCampaignsFailure({
          dispatch,
          launchError: LaunchError,
          functionName: "startCreateLeadForm",
          action: "createLeadForm",
          code: getCreateLeadFormFailureCode({ data }),
          context: { integrationID, adAccountID, platform },
        });
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      return {
        ok: true,
        data: {
          id: data.data?.id,
          name: data.data?.name,
        },
      };
    } catch (error) {
      reportCreateLeadFormAdsCampaignsFailure({
        dispatch,
        launchError: LaunchError,
        functionName: "startCreateLeadForm",
        action: "createLeadForm",
        code: getCreateLeadFormFailureCode({ error }),
        context: { integrationID, adAccountID, platform },
        fromException: true,
        status: error?.response?.status,
        errorName: error?.name,
      });
      return {
        ok: false,
      };
    }
  };

export const startCreateInstantForm =
  ({ integrationID, pageID, platform, formData }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createLeadFormAdsCampaigns")}/createLeadFormAdsCampaigns`,
        data: {
          integrationID,
          pageID,
          platform,
          formData,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        reportCreateLeadFormAdsCampaignsFailure({
          dispatch,
          launchError: LaunchError,
          functionName: "startCreateInstantForm",
          action: "createInstantForm",
          code: getCreateLeadFormFailureCode({ data }),
          context: { integrationID, pageID, platform },
        });
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      reportCreateLeadFormAdsCampaignsFailure({
        dispatch,
        launchError: LaunchError,
        functionName: "startCreateInstantForm",
        action: "createInstantForm",
        code: getCreateLeadFormFailureCode({ error }),
        context: { integrationID, pageID, platform },
        fromException: true,
        status: error?.response?.status,
        errorName: error?.name,
      });
      return {
        ok: false,
        message: error?.response?.data?.message,
      };
    }
  };
export const startGetCustomAudiences =
  ({
    platform,
    lastKey,
    integrationID,
    adAccountID,
    onChangeLastKey = () => {},
    onChangeLoadLastItem = () => {},
    name,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getCustomAudiencesAdsCampaigns")}/getCustomAudiencesAdsCampaigns`,
        data: {
          platform,
          lastKey,
          integrationID,
          adAccountID,
          name,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const response = data.data;

      if (!response?.hasMore) {
        onChangeLoadLastItem(true);
      }

      onChangeLastKey(response?.lastKey);

      return {
        ok: true,
        data: response?.customAudiences || [],
      };
    } catch (error) {
      onChangeLoadLastItem();
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetCustomAudiences",
          context: { platform, integrationID, adAccountID, name },
          tags: { action: "getCustomAudiences" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startGetSavedAudiences =
  ({
    platform,
    lastKey,
    integrationID,
    adAccountID,
    onChangeLastKey = () => {},
    onChangeLoadLastItem = () => {},
    name,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getSavedAudiencesAdsCampaigns")}/getSavedAudiencesAdsCampaigns`,
        data: {
          platform,
          lastKey,
          integrationID,
          adAccountID,
          name,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const response = data.data;

      if (!response?.hasMore) {
        onChangeLoadLastItem(true);
      }

      onChangeLastKey(response?.lastKey);

      return {
        ok: true,
        data: response?.savedAudiences || [],
      };
    } catch (error) {
      onChangeLoadLastItem();
      dispatch(LaunchError(error, false));
      return {
        ok: false,
      };
    }
  };

export const startCreateCustomAudience =
  (customAudience) => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createCustomAudienceAdsCampaign")}/createCustomAudienceAdsCampaign`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: customAudience,
      });

      if (!data.ok) {
        return {
          ok: false,
        };
      }

      const response = data.data;

      return { ok: true, data: response };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startCreateCustomAudience",
          context: {},
          tags: { action: "createCustomAudience" },
          level: "error",
        })
      );
      return {
        ok: false,
        code: errorFormatted.code,
        message: errorFormatted.message,
      };
    }
  };

export const startGetGalleryAdsPlatforms =
  ({
    platform,
    integrationID,
    adAccountID,
    pageID,
    socialNetworkURL,
    lastKey,
    folderID,
    limit,
    minDurationSeconds,
    maxDurationSeconds,
    authorType,
    adFormat,
    onChangeLastKey = () => {},
    onChangeLoadLastItem = () => {},
    onChangeIsCalling = () => {},
    from = ADS_CAMPAIGN_MEDIA_FROM_PHYLLO,
    signal,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      onChangeIsCalling(true);
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getGalleryMediaFromPlatforms")}/getGalleryMediaFromPlatforms`,
        data: {
          platform,
          integrationID,
          ...(adAccountID && { adAccountID }),
          ...(pageID && { pageID }),
          socialNetworkURL,
          from,
          lastKey,
          ...(authorType && { authorType }),
          ...(adFormat && { adFormat }),
          ...(limit && { limit }),
          ...(folderID && { folderID }),
          ...(minDurationSeconds != null && { minDurationSeconds }),
          ...(maxDurationSeconds != null && { maxDurationSeconds }),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });
      onChangeIsCalling(false);

      if (!data.ok) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const response = data.data;

      if (!response?.hasMore) {
        onChangeLoadLastItem(true);
      }
      onChangeLastKey(response?.lastKey);

      return {
        ok: true,
        data: response?.media || [],
      };
    } catch (error) {
      if (isAbortError(error)) {
        return { ok: false, aborted: true };
      }
      onChangeLoadLastItem(true);
      onChangeIsCalling(false);
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetGalleryAdsPlatforms",
          context: { platform, integrationID, adAccountID, pageID, from },
          tags: { action: "getGalleryAdsPlatforms" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startGetIdentitiesTikTokBusiness =
  ({ objective, adAccountID, integrationID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getIdentitiesTikTokBusiness")}/getIdentitiesTikTokBusiness`,
        data: {
          objective,
          integrationID,
          adAccountID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const identities = data.data;

      return {
        ok: true,
        data: identities,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetIdentitiesTikTokBusiness",
          context: { objective, integrationID, adAccountID },
          tags: { action: "getIdentitiesTikTokBusiness" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startCreateIdentityTikTokBusiness =
  ({ name, imageURL, integrationID, adAccountID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createIdentityTikTokBusiness")}/createIdentityTikTokBusiness`,
        data: {
          name,
          imageURL,
          integrationID,
          adAccountID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const identity = data.data;

      return {
        ok: true,
        data: identity,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startCreateIdentityTikTokBusiness",
          context: { name, integrationID, adAccountID },
          tags: { action: "createIdentityTikTokBusiness" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetTrackingCustomEvents =
  ({
    platform,
    lastKey,
    integrationID,
    adAccountID,
    trackingID,
    onChangeLastKey = () => {},
    onChangeLoadLastItem = () => {},
    objective,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getTrackingCustomEventsAdsCampaigns")}/getTrackingCustomEventsAdsCampaigns`,
        data: {
          platform,
          lastKey,
          integrationID,
          trackingID,
          adAccountID,
          objective,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const response = data.data;

      if (!response?.hasMore) {
        onChangeLoadLastItem(true);
      }

      onChangeLastKey(response?.lastKey);

      return {
        ok: true,
        data: response?.trackingCustomEvents || [],
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetTrackingCustomEvents",
          context: { platform, integrationID, trackingID, adAccountID },
          tags: { action: "getTrackingCustomEvents" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetAdPreview =
  (adPreviewData, signal) => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getAdPreviewAdsCampaigns")}/getAdPreviewAdsCampaigns`,
        data: adPreviewData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      if (!data.ok) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const adsPreviewsData = data.data;

      return {
        ok: true,
        data: adsPreviewsData,
      };
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Request aborted");
        return { ok: false, aborted: true };
      }
      const response = dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdPreview",
          context: {},
          tags: { action: "getAdPreview" },
          level: "error",
        })
      );
      return response;
    }
  };

export const startGetSuggestedLocations =
  (campaignContext) => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getSuggestedLocations")}/getSuggestedLocations`,
        data: { campaignContext },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetSuggestedLocations",
          context: {},
          tags: { action: "getSuggestedLocations" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetLocationsByPrompt =
  (locationsPrompt) => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getLocationsByPrompt")}/getLocationsByPrompt`,
        data: { locationsPrompt },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetLocationsByPrompt",
          context: {},
          tags: { action: "getLocationsByPrompt" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startExtractBulkCampaignStructure =
  ({ fileName, base64Data, contentIDs = [], onJobCreated }) =>
  async (dispatch) => {
    try {
      return await createCreativeJobAndWaitForResponse({
        dispatch,
        jobType: BULK_CAMPAIGNS_JOB_TYPE.analyze,
        payload: {
          fileName,
          base64Data,
          contentIDs: toBulkContentIDs(contentIDs),
        },
        onJobCreated,
      });
    } catch (error) {
      // Job errors carry the worker response; createJob failures are axios errors.
      const errorData = error?.response?.data ?? error;
      const code = errorData?.code;
      const platform = errorData?.platform;
      // Expected validation errors (file type / size / unsupported platform or
      // campaign type): the modal explains them.
      if (!BULK_CAMPAIGNS_EXPECTED_ERROR_CODES.includes(code)) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "startExtractBulkCampaignStructure",
            context: { fileName },
            tags: { action: "extractBulkCampaignStructure" },
            level: "error",
          })
        );
      }
      return { ok: false, code, platform };
    }
  };

export const startCreateBulkCampaigns =
  ({ platform, campaignType, campaigns, onJobCreated }) =>
  async (dispatch) => {
    try {
      return await createCreativeJobAndWaitForResponse({
        dispatch,
        jobType: BULK_CAMPAIGNS_JOB_TYPE.generate,
        payload: {
          platform,
          campaignType,
          campaigns,
          campaignCount: campaigns?.length || 0,
        },
        onJobCreated,
      });
    } catch (error) {
      const code = error?.response?.data?.code ?? error?.code;
      if (!BULK_CAMPAIGNS_GENERATION_EXPECTED_ERROR_CODES.includes(code)) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "startCreateBulkCampaigns",
            context: { platform, campaignType, campaigns: campaigns?.length },
            tags: { action: "createBulkCampaigns" },
            level: "error",
          })
        );
      }
      return {
        ok: false,
        code,
      };
    }
  };
export const startVerifyUrlCanBeScrape = (urlToScrape) => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("verifyUrlCanBeScraped")}/verifyUrlCanBeScraped`,
      data: { url: urlToScrape },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data.ok;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startVerifyUrlCanBeScrape",
        context: { urlToScrape },
        tags: { action: "verifyUrlCanBeScrape" },
        level: "error",
      })
    );
    return false;
  }
};
export const startExtractBrandInformationFromUrl =
  (
    urlToScrape,
    { skipTypographyPreview = false, prompt = "", fileUrl = "" } = {}
  ) =>
  async (dispatch, getState) => {
    try {
      const isPlanActive = getState()?.payments?.isPlanActive;
      const isTrial = getState()?.payments?.subscriptionPlanInfo?.isTrial;
      const shouldValidateBlockedBrands = shouldApplyBlockedBrandsValidation({
        isPlanActive,
        isTrial,
      });
      if (shouldValidateBlockedBrands) {
        const blockedBrand = getBlockedBrandFromText({
          text: `${urlToScrape} ${prompt}`,
        });
        if (blockedBrand) {
          throw {
            code: ERROR_OCCURRED,
            message: i18next.t(ERROR_OCCURRED),
          };
        }
      }

      const token = await dispatch(renewToken());
      const language = getLanguageFormatted(i18next.language);

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("extractBrandInformationFromUrl")}/extractBrandInformationFromUrl`,
        data: {
          url: urlToScrape,
          language,
          skipTypographyPreview: Boolean(skipTypographyPreview),
          ...(prompt ? { prompt } : {}),
          ...(fileUrl ? { fileUrl } : {}),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data;
    } catch (error) {
      const status = error?.response?.status;
      const isServerError = status >= 500;

      dispatch(
        LaunchError(error, true, [], {
          functionName: "startExtractBrandInformationFromUrl",
          context: { urlToScrape },
          tags: { action: "extractBrandInformationFromUrl" },
          level: "error",
          isWarning: true,
          ...(isServerError
            ? {
                resolvedMessage: i18next.t(
                  ERROR_GETTING_URL_INFORMATION_BY_PROTECTED
                ),
              }
            : {}),
        })
      );
      return false;
    }
  };

export const startDetectAndSaveBrandLanguage = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("detectAndSaveBrandLanguage")}/detectAndSaveBrandLanguage`,
      data: {},
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startDetectAndSaveBrandLanguage",
        context: {},
        tags: { action: "detectAndSaveBrandLanguage" },
        level: "warning",
        isWarning: true,
      })
    );
    return { ok: false };
  }
};

export const startClassifyAdvertiserSeasonalEvents = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("classifyAdvertiserSeasonalEvents")}/classifyAdvertiserSeasonalEvents`,
      data: {},
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startClassifyAdvertiserSeasonalEvents",
        context: {},
        tags: { action: "classifyAdvertiserSeasonalEvents" },
        level: "warning",
        isWarning: true,
      })
    );
    return { ok: false };
  }
};

export const startAnalyzeBusinessProductImages = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("analyzeBusinessProductImages")}/analyzeBusinessProductImages`,
      data: {},
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startAnalyzeBusinessProductImages",
        context: {},
        tags: { action: "analyzeBusinessProductImages" },
        level: "warning",
        isWarning: true,
      })
    );
    return { ok: false };
  }
};

export const startSaveBrandInformationFromUrl =
  ({
    websiteUrl,
    socialNetworks,
    businessName,
    businessDescription,
    businessOffering,
    businessLookAndFeel,
    businessValueProposition,
    businessBuyerPerson,
    businessIdealCustomerProfile,
    businessColors,
    businessTextStyle,
    businessVoiceAttributes,
    logoUrl,
    businessProductImages,
    additionalAssets,
    businessCompetitors,
    businessNameLowercase,
    businessTypography,
    businessTypographyPreview,
    businessCountry,
  }) =>
  async (dispatch) => {
    try {
      const { pathBrandInformation } = dispatch(getPathDatabaseByRole());

      const updates = {};

      updates[`${pathBrandInformation}/websiteUrl`] = websiteUrl || "";
      updates[`${pathBrandInformation}/socialNetworks`] = socialNetworks || [];
      updates[`${pathBrandInformation}/businessName`] = businessName || "";
      updates[`${pathBrandInformation}/businessDescription`] =
        businessDescription || "";
      updates[`${pathBrandInformation}/businessTextStyle`] =
        businessTextStyle || "";
      updates[`${pathBrandInformation}/businessVoiceAttributes`] =
        businessVoiceAttributes || [];
      updates[`${pathBrandInformation}/businessOffering`] =
        businessOffering || "";
      updates[`${pathBrandInformation}/businessLookAndFeel`] =
        businessLookAndFeel || "";
      updates[`${pathBrandInformation}/businessValueProposition`] =
        businessValueProposition || "";
      updates[`${pathBrandInformation}/businessIdealCustomerProfile`] =
        businessIdealCustomerProfile || "";
      updates[`${pathBrandInformation}/businessBuyerPerson`] =
        businessBuyerPerson || "";
      updates[`${pathBrandInformation}/businessColors`] = businessColors || [];
      updates[`${pathBrandInformation}/logoUrl`] = logoUrl || "";
      updates[`${pathBrandInformation}/businessProductImages`] =
        businessProductImages || [];
      updates[`${pathBrandInformation}/additionalAssets`] =
        additionalAssets || [];
      updates[`${pathBrandInformation}/businessCompetitors`] =
        businessCompetitors || [];
      updates[`${pathBrandInformation}/businessNameLowercase`] =
        businessNameLowercase || "";
      updates[`${pathBrandInformation}/businessCountry`] =
        businessCountry || "";

      // Incluir businessTypography si existe
      if (businessTypography && typeof businessTypography === "object") {
        updates[`${pathBrandInformation}/businessTypography`] =
          businessTypography;
      }

      // Incluir businessTypographyPreview si existe
      if (businessTypographyPreview) {
        updates[`${pathBrandInformation}/businessTypographyPreview`] =
          businessTypographyPreview;
      }

      await update(ref(db), updates);

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveBrandInformationFromUrl",
          context: {},
          tags: { action: "saveBrandInformationFromUrl" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startSaveBrandAllowlistField =
  (field, value) => async (dispatch) => {
    try {
      if (!BRAND_INSIGHT_ALLOWLIST_FIELDS.includes(field)) {
        return { ok: false };
      }
      const { pathBrandInformation } = dispatch(getPathDatabaseByRole());
      const updates = {};
      updates[`${pathBrandInformation}/${field}`] = value ?? "";
      await update(ref(db), updates);
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveBrandAllowlistField",
          context: { field },
          tags: { action: "saveBrandAllowlistField" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startSaveBrandLookAndFeel =
  (businessLookAndFeel) => async (dispatch) => {
    try {
      const { pathBrandInformation } = dispatch(getPathDatabaseByRole());
      const updates = {};
      updates[`${pathBrandInformation}/businessLookAndFeel`] =
        businessLookAndFeel || "";
      await update(ref(db), updates);
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveBrandLookAndFeel",
          context: {},
          tags: { action: "saveBrandLookAndFeel" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startSaveBrandLogo = (logoUrl) => async (dispatch) => {
  try {
    const { pathBrandInformation } = dispatch(getPathDatabaseByRole());
    const updates = {};
    updates[`${pathBrandInformation}/logoUrl`] = logoUrl || "";
    await update(ref(db), updates);
    return { ok: true };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startSaveBrandLogo",
        context: {},
        tags: { action: "saveBrandLogo" },
        level: "error",
      })
    );
    return { ok: false };
  }
};

export const startSaveBusinessCompetitors =
  (businessCompetitors) => async (dispatch) => {
    try {
      const { pathBrandInformation } = dispatch(getPathDatabaseByRole());
      const updates = {};
      updates[`${pathBrandInformation}/businessCompetitors`] =
        businessCompetitors || [];
      await update(ref(db), updates);
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveBusinessCompetitors",
          context: {},
          tags: { action: "saveBusinessCompetitors" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startGetBrandInformation = () => async (dispatch) => {
  try {
    const { pathBrandInformation } = dispatch(getPathDatabaseByRole());

    const fields = [
      "websiteUrl",
      "socialNetworks",
      "businessName",
      "businessDescription",
      "businessTextStyle",
      "businessVoiceAttributes",
      "businessLookAndFeel",
      "businessValueProposition",
      "businessOffering",
      "businessIdealCustomerProfile",
      "businessBuyerPerson",
      "businessCompetitors",
      "logoUrl",
      "businessProductImages",
      "additionalAssets",
      "businessCountry",
      "businessNameLowercase",
      "businessColors",
      "businessTypography",
      "businessTypographyPreview",
      "hasAdvancedConfigurationAdditionalAssets",
      "hasAdditionalAssets",
      "requiresTypographicFidelity",
      "language",
    ];

    const promises = [];

    fields.forEach((field) => {
      promises.push(get(ref(db, `${pathBrandInformation}/${field}`)));
    });

    const snapshots = await Promise.allSettled(promises);

    const data = {};

    snapshots.forEach((snapshot, index) => {
      if (snapshot.status !== "fulfilled") return;

      const value = snapshot.value.val();
      data[fields[index]] = value;
    });

    if (_.isEmpty(data)) {
      return { ok: false };
    }

    if (data.language) {
      dispatch(startSetAdvertiserLanguage(data.language));
    }

    return { ok: true, data };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetBrandInformation",
        context: {},
        tags: { action: "getBrandInformation" },
        level: "error",
      })
    );
    return { ok: false };
  }
};

export const startGenerateTypographyPreview =
  (businessTypography) => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      if (!token) {
        return { ok: false };
      }

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("generateTypographyPreview")}/generateTypographyPreview`,
        data: {
          businessTypography: {
            fontFamily: businessTypography?.fontFamily,
            bodyFontFamily: businessTypography?.bodyFontFamily,
            sizes: {
              heading: businessTypography?.sizes?.heading,
              body: businessTypography?.sizes?.body,
            },
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return { ok: false };
      }

      return { ok: true, data: { previewUrl: data.data?.previewUrl } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGenerateTypographyPreview",
          context: {
            businessTypography: {
              fontFamily: businessTypography?.fontFamily,
              hasSizes: !!businessTypography?.sizes,
            },
          },
          tags: { action: "generateTypographyPreview" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
export const startGetBusinessOffering = () => async (dispatch) => {
  try {
    const { pathBrandInformation } = dispatch(getPathDatabaseByRole());

    const snapshot = await get(
      ref(db, `${pathBrandInformation}/businessOffering`)
    );

    const businessOffering = snapshot.val();

    if (!businessOffering) {
      return { ok: false };
    }

    return { ok: true, data: businessOffering };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetBusinessOffering",
        context: {},
        tags: { action: "getBusinessOffering" },
        level: "error",
      })
    );
    return { ok: false };
  }
};

export const startGetWhatsappBusinessPhoneNumbers =
  ({ integrationID, adAccountID, pageID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getWhatsappBusinessPhoneNumbersAdsCampaigns")}/getWhatsappBusinessPhoneNumbersAdsCampaigns`,
        data: {
          integrationID,
          adAccountID,
          pageID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const response = data.data;

      return {
        ok: true,
        data: response || [],
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetWhatsappBusinessPhoneNumbers",
          context: { integrationID, adAccountID, pageID },
          tags: { action: "getWhatsappBusinessPhoneNumbers" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startSaveOptimizationInLog =
  ({ campaignID, platform, optimization }) =>
  async (dispatch) => {
    try {
      if (ADS_CAMPAIGNS_DEMO.includes(campaignID)) return { ok: true };

      const userID = dispatch(getUserID());
      const { pathCampaignLogs } = dispatch(getPathDatabaseByRole());
      const updates = {};

      const logID = push(ref(db, `${pathCampaignLogs}/${campaignID}/logs`)).key;
      updates[`${pathCampaignLogs}/${campaignID}/${logID}`] = {
        campaignID,
        userID,
        platform,
        creationTime: new Date().getTime(),
        creationTimeNegative: new Date().getTime() * -1,
        changes: [
          {
            field: "insight",
            objectType: "insight",
            typeChange: "single",
            type: "update",
            newValue: optimization,
          },
        ],
      };

      await update(ref(db), updates);

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveOptimizationInLog",
          context: { campaignID, platform },
          tags: { action: "saveOptimizationInLog" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
export const startSetRecommendedActionIsApplied =
  ({ campaignID, platform, actionIndex, isApplied }) =>
  async (dispatch) => {
    try {
      const { pathCampaigns } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(
        db,
        `${pathCampaigns}/${campaignID}/platformsData/${platform}/recommendedActions/${actionIndex}/isApplied`
      );
      await set(dbRef, isApplied);
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSetRecommendedActionIsApplied",
          context: { campaignID, platform, actionIndex },
          tags: { action: "setRecommendedActionIsApplied" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startGetAdsCampaignsFromPlatform =
  ({
    platform,
    integrationID,
    adAccountID,
    lastKey,
    onChangeLastKey = () => {},
    onChangeLoadLastItem = () => {},
    signal,
    statusFilter,
    name,
    startDateTime,
    endDateTime,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      let isValidData = false;
      let currentLastKey = lastKey;
      const campaignsData = [];

      while (!isValidData) {
        const { data } = await axios({
          method: "post",
          url: `${getCloudFunctionUrl("getAdsCampaignsFromPlatform")}/getAdsCampaignsFromPlatform`,
          data: {
            platform,
            integrationID,
            adAccountID,
            lastKey: currentLastKey,
            name,
            startDateTime,
            endDateTime,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        });

        const { campaigns, hasMore, lastKey: newLastKey } = data.data;

        campaignsData.push(...campaigns);

        const filteredCampaigns = statusFilter
          ? campaignsData.filter((campaign) =>
              applyFiltersImportAdsCampaigns({
                campaign,
                status: statusFilter,
                name,
                filterByName: false,
              })
            )
          : campaignsData;

        // Check if we have enough campaigns or if there are no more
        if (filteredCampaigns.length >= 10 || !hasMore) {
          isValidData = true;
          if (!hasMore) {
            onChangeLoadLastItem(true);
          }
        } else {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        currentLastKey = newLastKey;
      }

      onChangeLastKey(currentLastKey);
      return { ok: true, data: campaignsData };
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Request aborted");
        return { ok: false, aborted: true };
      }
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdsCampaignsFromPlatform",
          context: { platform, integrationID, adAccountID, name },
          tags: { action: "getAdsCampaignsFromPlatform" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startImportAdsCampaign =
  ({
    platform,
    integrationID,
    adAccountID,
    campaignID,
    duplicateStructureOnly = false,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("importAdsCampaign")}/importAdsCampaign`,
        data: {
          platform,
          integrationID,
          adAccountID,
          campaignID,
          duplicateStructureOnly,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: {
          campaign: data.data.campaign,
          campaignID: data.data.campaignID,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startImportAdsCampaign",
          context: { platform, integrationID, adAccountID, campaignID },
          tags: { action: "importAdsCampaign" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startExportCopiesToGoogleDrive =
  ({ campaignID, platform, assetGroupID, adID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("exportCopiesToGoogleDrive")}/exportCopiesToGoogleDrive`,
        data: {
          campaignID,
          platform,
          assetGroupID,
          adID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: {
          url: data.data.urlExcel,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startExportCopiesToGoogleDrive",
          context: { campaignID, platform, assetGroupID, adID },
          tags: { action: "exportCopiesToGoogleDrive" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startGetAudienceProjectionAdsCampaign =
  ({
    locations,
    objective,
    platform,
    budget,
    typeBudget,
    currency,
    startDate,
    endDate,
    signal,
  }) =>
  async (dispatch) => {
    try {
      const language = getLanguageFormatted(i18next.language);

      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getAudienceProjectionAdsCampaign")}/getAudienceProjectionAdsCampaign`,
        data: {
          locations,
          objective,
          platform,
          budget,
          typeBudget,
          language,
          currency,
          startDate,
          endDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return {
        ok: true,
        data: {
          text: data.data.text,
        },
      };
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Request aborted");
        return { ok: false, aborted: true };
      }
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAudienceProjectionAdsCampaign",
          context: { platform, objective },
          tags: { action: "getAudienceProjectionAdsCampaign" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetGlobalReportGoalsProjection =
  ({ historicalDaily, forecastDays, signal }) =>
  async (dispatch) => {
    try {
      const language = getLanguageFormatted(i18next.language);
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getGlobalReportGoalsProjection")}/getGlobalReportGoalsProjection`,
        data: {
          historicalDaily,
          forecastDays,
          language,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return {
        ok: true,
        data: {
          forecastDailyResults: data.data.forecastDailyResults,
        },
      };
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return { ok: false, aborted: true };
      }
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetGlobalReportGoalsProjection",
          context: { forecastDays },
          tags: { action: "getGlobalReportGoalsProjection" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetGlobalReportSuggestedKpi = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("getGlobalReportSuggestedKpi")}/getGlobalReportSuggestedKpi`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      ok: true,
      data: {
        primaryKpi: data?.data?.primaryKpi || null,
      },
    };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetGlobalReportSuggestedKpi",
        tags: { action: "getGlobalReportSuggestedKpi" },
        level: "error",
      })
    );
    return {
      ok: false,
    };
  }
};

export const startGetSuggestedCreativesByBudgetAdsCampaign =
  ({ platform, budget, typeBudget, currency, startDate, endDate }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getSuggestedCreativesByBudgetAdsCampaign")}/getSuggestedCreativesByBudgetAdsCampaign`,
        data: {
          platform,
          budget,
          typeBudget,
          currency,
          startDate,
          endDate,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return {
        ok: true,
        data: {
          suggestedCreativesByBudget: data.data.suggestedCreativesByBudget,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetSuggestedCreativesByBudgetAdsCampaign",
          context: { platform, budget, typeBudget },
          tags: { action: "getSuggestedCreativesByBudgetAdsCampaign" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startGetUnifiedCampaignStatistics =
  ({
    campaignIDs,
    levels,
    breakdowns,
    startDateTime,
    endDateTime,
    options,
    forceShowIntegrationNotFound = false,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const requestOptions = {
        ...(options || {}),
        ...(forceShowIntegrationNotFound
          ? { forceReportFailedIntegrations: true }
          : {}),
      };

      const requestBody = {
        campaignIDs,
        ...(levels ? { levels } : {}),
        ...(breakdowns ? { breakdowns } : {}),
        ...(Object.keys(requestOptions).length > 0
          ? { options: requestOptions }
          : {}),
      };

      if (startDateTime != null) {
        requestBody.startDateTime = formatDateForStatistics(startDateTime);
      }
      if (endDateTime != null) {
        requestBody.endDateTime = formatDateForStatistics(endDateTime);
      }

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getUnifiedCampaignStatistics")}/getUnifiedCampaignStatistics`,
        data: requestBody,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        openIntegrationNotFoundModalIfNeeded(data?.data?.failedIntegrations, {
          forceShow: forceShowIntegrationNotFound,
        })
      );

      return {
        ok: data.ok,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(
          error,
          false,
          [],
          {
            functionName: "startGetUnifiedCampaignStatistics",
            context: {
              campaignIDs,
              levels,
              breakdowns,
              startDateTime,
              endDateTime,
            },
            tags: { action: "getUnifiedCampaignStatistics" },
            level: "error",
          },
          {
            forceShowIntegrationNotFound,
          }
        )
      );
      return {
        ok: false,
      };
    }
  };

export const startGetStatisticsAdsCampaign =
  ({ campaignID, startDateTime, endDateTime }) =>
  async (dispatch) => {
    try {
      const response = await dispatch(
        startGetUnifiedCampaignStatistics({
          campaignIDs: [campaignID],
          levels: LEGACY_SINGLE_CAMPAIGN_STATS_LEVELS,
          breakdowns: LEGACY_SINGLE_CAMPAIGN_STATS_BREAKDOWNS,
          startDateTime,
          endDateTime,
        })
      );

      if (!response.ok) {
        return { ok: false };
      }

      return {
        ok: true,
        data: mapUnifiedToLegacySingleCampaignStats(response.data, campaignID),
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetStatisticsAdsCampaign",
          context: { campaignID, startDateTime, endDateTime },
          tags: { action: "getStatisticsAdsCampaign" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetStatisticsAdsCampaigns =
  ({
    campaignIDs,
    startDateTime,
    endDateTime,
    includeTimeSeries,
    includeAdsLevels = false,
  }) =>
  async (dispatch) => {
    try {
      const levels = includeAdsLevels
        ? GLOBAL_REPORT_STATS_WITH_ADS_LEVELS
        : LEGACY_CAMPAIGNS_STATS_LEVELS;

      const breakdowns = includeTimeSeries
        ? includeAdsLevels
          ? GLOBAL_REPORT_STATS_WITH_ADS_BREAKDOWNS
          : LEGACY_CAMPAIGNS_TIME_SERIES_BREAKDOWNS
        : LEGACY_CAMPAIGNS_STATS_BREAKDOWNS;

      const response = await dispatch(
        startGetUnifiedCampaignStatistics({
          campaignIDs,
          levels,
          breakdowns,
          startDateTime,
          endDateTime,
        })
      );

      if (!response.ok) {
        return { ok: false };
      }

      const legacyData = mapUnifiedToLegacyCampaignsStatsMap(response.data);
      const timeSeries = includeTimeSeries
        ? mapUnifiedToLegacyTimeSeries(response.data, campaignIDs)
        : undefined;

      return {
        ok: true,
        data: legacyData,
        timeSeries,
        periodLast: undefined,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetStatisticsAdsCampaigns",
          context: {
            campaignIDs,
            startDateTime,
            endDateTime,
            includeTimeSeries,
          },
          tags: { action: "getStatisticsAdsCampaigns" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetTotalStatisticsAllCampaigns =
  (params = {}) =>
  async (dispatch, getState) => {
    try {
      const token = await dispatch(renewToken(false));
      const { totalMetrics } = getState().user;

      if (
        !_.isEmpty(totalMetrics) &&
        !params?.startDateTime &&
        !params?.endDateTime &&
        !params?.measurableKpi &&
        !params?.folderID
      ) {
        const now = new Date().getTime();
        const lastUpdateTime = totalMetrics.lastUpdateTime;

        const differenceMinutes = differenceInMinutes(now, lastUpdateTime);
        if (differenceMinutes < 10) {
          return {
            ok: true,
            data: { totals: totalMetrics },
          };
        }
      }
      const requestData = {
        ...params,
        ...(params?.startDateTime != null && {
          startDateTime: formatDateForStatistics(params.startDateTime),
        }),
        ...(params?.endDateTime != null && {
          endDateTime: formatDateForStatistics(params.endDateTime),
        }),
      };

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getTotalMetricsForAllCampaign")}/getTotalMetricsForAllCampaign`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: requestData,
      });

      if (
        !params?.startDateTime &&
        !params?.endDateTime &&
        !params?.measurableKpi &&
        !params?.folderID
      ) {
        dispatch(
          getTotalMetricsCampaigns({
            ...(data?.data?.totals || {}),
            lastUpdateTime: new Date().getTime(),
          })
        );
      }

      //console.log("data", data.data);

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(LaunchError(error, false));
      return {
        ok: false,
      };
    }
  };

export const getTotalMetricsCampaigns = (data) => ({
  type: types.GET_TOTAL_METRICS_CAMPAIGNS,
  payload: data,
});

export const startGetKpiTargetCpaStatus = () => async (dispatch) => {
  try {
    const { userID } = dispatch(getPathDatabaseByRole());
    if (!userID) {
      return { ok: false };
    }

    const kpiSnapshot = await get(ref(db, `advertisers/${userID}/kpiSummary`));
    const fingerprint = buildKpiTargetCpaFingerprint(kpiSnapshot.val());
    if (!fingerprint) {
      return { ok: true, data: { platforms: [], skipped: true } };
    }

    try {
      const cacheSnapshot = await get(
        ref(db, `advertisers/${userID}/kpiTargetCpaCheck`)
      );
      const cache = cacheSnapshot.val();
      if (isKpiTargetCpaCacheFresh(cache, fingerprint)) {
        return {
          ok: true,
          data: {
            platforms: Array.isArray(cache.platforms) ? cache.platforms : [],
            lastCheckedAt: cache.lastCheckedAt,
            fromCache: true,
          },
        };
      }
    } catch (cacheError) {
      dispatch(
        LaunchError(cacheError, false, [], {
          functionName: "startGetKpiTargetCpaStatus",
          capture: "exception",
        })
      );
      console.log(cacheError);
    }

    const token = await dispatch(renewToken(false));
    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("getKpiTargetCpaStatus")}/getKpiTargetCpaStatus`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      ok: true,
      data: data?.data || { platforms: [] },
    };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetKpiTargetCpaStatus",
        tags: { action: "getKpiTargetCpaStatus" },
        level: "error",
      })
    );
    return { ok: false };
  }
};

export const startGetStatisticsConfigurationAdsCampaign =
  ({ campaignID }) =>
  async (dispatch) => {
    try {
      const { pathCampaignStatistics } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `${pathCampaignStatistics}/${campaignID}`);
      const snapshot = await get(dbRef);

      if (!snapshot.exists()) {
        return {
          ok: false,
        };
      }

      return {
        ok: true,
        data: snapshot.val(),
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetStatisticsConfigurationAdsCampaign",
          context: { campaignID },
          tags: { action: "getStatisticsConfigurationAdsCampaign" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetCampaignContext = (campaignID) => async (dispatch) => {
  try {
    const { pathCampaigns } = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, `${pathCampaigns}/${campaignID}/context`);
    const snapshot = await get(dbRef);

    if (!snapshot.exists()) {
      return {
        ok: false,
      };
    }

    const data = snapshot.val();
    return {
      ok: true,
      context: data,
    };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetCampaignContext",
        context: { campaignID },
        tags: { action: "getCampaignContext" },
        level: "error",
      })
    );
    return {
      ok: false,
    };
  }
};

export const startGetCampaignLogs =
  ({ campaignID, lastKey, onChangeLastKey = () => {} }) =>
  async (dispatch) => {
    try {
      const { pathCampaignLogs } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(db, `${pathCampaignLogs}/${campaignID}`);
      let q;

      if (lastKey) {
        q = query(
          dbRef,
          orderByChild("creationTimeNegative"),
          startAfter(lastKey),
          limitToFirst(20)
        );
      } else {
        q = query(
          dbRef,
          orderByChild("creationTimeNegative"),
          limitToFirst(20)
        );
      }

      const snapshot = await get(q);

      if (!snapshot.exists()) {
        return {
          ok: true,
          data: [],
          hasMore: false,
        };
      }

      let data = {};
      let lastLog = null;

      snapshot.forEach((childSnapshot) => {
        const logKey = childSnapshot.key;
        const logData = childSnapshot.val();
        data[logKey] = logData;
        lastLog = logData;
      });

      if (lastLog) {
        onChangeLastKey(lastLog.creationTimeNegative);
      }

      const logs = Object.values(data);
      const hasMore = logs.length === 20;

      return {
        ok: true,
        data: data,
        hasMore,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetCampaignLogs",
          context: { campaignID },
          tags: { action: "getCampaignLogs" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetItemName =
  ({ objectType, objectID }) =>
  async (dispatch) => {
    try {
      const { pathCampaigns, pathAdSets, pathAssetsGroups, pathAds } = dispatch(
        getPathDatabaseByRole()
      );

      let dbRef;
      if (objectType === ADS_OBJECT_TYPES.campaign) {
        dbRef = ref(db, `${pathCampaigns}/${objectID}/name`);
      } else if (objectType === ADS_OBJECT_TYPES.adGroup) {
        dbRef = ref(db, `${pathAdSets}/${objectID}/name`);
      } else if (objectType === ADS_OBJECT_TYPES.assetsGroup) {
        dbRef = ref(db, `${pathAssetsGroups}/${objectID}/name`);
      } else if (objectType === ADS_OBJECT_TYPES.ad) {
        dbRef = ref(db, `${pathAds}/${objectID}/name`);
      }

      if (!dbRef) {
        return {
          ok: false,
        };
      }

      const snapshot = await get(dbRef);

      if (!snapshot.exists()) {
        return {
          ok: false,
        };
      }

      return {
        ok: true,
        data: snapshot.val(),
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetMerchantsGoogleAds",
          context: { objectType, objectID },
          tags: { action: "getMerchantsGoogleAds" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetCatalogsAdsCampaigns =
  ({
    integrationID,
    adAccountID,
    catalogID,
    name,
    includedID,
    from,
    lastKey,
    platform,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getCatalogsAdsCampaigns")}/getCatalogsAdsCampaigns`,
        data: {
          integrationID,
          adAccountID,
          catalogID,
          name,
          includedID,
          from,
          lastKey,
          platform,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetCatalogsAdsCampaigns",
          context: {
            integrationID,
            adAccountID,
            catalogID,
            name,
            includedID,
            from,
            lastKey,
          },
          tags: { action: "getCatalogsAdsCampaigns" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetProductSetsCatalogAdsCampaigns =
  ({ integrationID, adAccountID, catalogID, lastKey, platform }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getProductSetsCatalogAdsCampaigns")}/getProductSetsCatalogAdsCampaigns`,
        data: {
          integrationID,
          adAccountID,
          catalogID,
          lastKey,
          platform,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetProductSetsCatalogAdsCampaigns",
          context: {
            integrationID,
            adAccountID,
            catalogID,
            lastKey,
          },
          tags: { action: "getProductSetsCatalogAdsCampaigns" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startGetProductsCatalogAdsCampaigns =
  ({ integrationID, adAccountID, catalogID, name, lastKey, platform }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getProductsCatalogAdsCampaigns")}/getProductsCatalogAdsCampaigns`,
        data: {
          integrationID,
          adAccountID,
          catalogID,
          name,
          lastKey,
          platform,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetProductSetsCatalogAdsCampaigns",
          context: {
            integrationID,
            adAccountID,
            catalogID,
            lastKey,
          },
          tags: { action: "getProductSetsCatalogAdsCampaigns" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startSearchApps =
  ({ store, search, limit, isSearchByID = false }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("searchApps")}/searchApps`,
        data: {
          store,
          search,
          limit,
          isSearchByID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: data.ok,
        data: data.data?.results || [],
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSearchApps",
          context: { store, search, limit },
          tags: { action: "searchApps" },
          level: "error",
        })
      );
      return {
        ok: false,
        data: [],
      };
    }
  };

export const startGetAdvertisableAppsAdsCampaigns =
  ({ integrationID, adAccountID, appID, platform }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getAdvertisableApplications")}/getAdvertisableApplications`,
        data: {
          integrationID,
          adAccountID,
          appID,
          platform,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdvertisableAppsAdsCampaigns",
          context: { integrationID, adAccountID, appID, platform },
          tags: { action: "getAdvertisableAppsAdsCampaigns" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startGetAppEvents =
  ({ integrationID, adAccountID, appID, platform, campaignType }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getAppEvents")}/getAppEvents`,
        data: {
          integrationID,
          adAccountID,
          appID,
          platform,
          campaignType,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAppEvents",
          context: {
            integrationID,
            adAccountID,
            appID,
            platform,
            campaignType,
          },
          tags: { action: "getAppEvents" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startGetOfflineEvents =
  ({ integrationID, adAccountID, platform }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getOfflineEvents")}/getOfflineEvents`,
        data: {
          integrationID,
          adAccountID,
          platform,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetOfflineEvents",
          context: { integrationID, adAccountID, platform },
          tags: { action: "getOfflineEvents" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetInstantExperiences =
  ({
    integrationID,
    pageID,
    instantExperienceID,
    lastKey,
    onChangeLastKey = () => {},
    onChangeLoadLastItem = () => {},
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getInstantExperiences")}/getInstantExperiences`,
        data: {
          integrationID,
          pageID,
          instantExperienceID,
          lastKey,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const response = data.data;

      if (!response?.hasMore) {
        onChangeLoadLastItem(true);
      }

      onChangeLastKey(response?.lastKey);

      return {
        ok: true,
        data: response?.instantExperiences || [],
      };
    } catch (error) {
      onChangeLoadLastItem();
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetInstantExperiences",
          context: { integrationID, pageID, instantExperienceID },
          tags: { action: "getInstantExperiences" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startCreateInstantExperience =
  ({
    integrationID,
    pageID,
    adAccountID,
    productSetID,
    mediaUrl,
    callToAction,
    destinationUrl,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createInstantExperiences")}/createInstantExperiences`,
        data: {
          integrationID,
          pageID,
          adAccountID,
          productSetID,
          mediaUrl,
          callToAction,
          destinationUrl,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: data.ok,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startCreateInstantExperience",
          context: { integrationID, pageID, adAccountID, productSetID },
          tags: { action: "createInstantExperience" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetGoogleAdsAccountExclusions =
  ({ integrationID, adAccountID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getGoogleAdsAccountExclusions")}/getGoogleAdsAccountExclusions`,
        data: {
          integrationID,
          adAccountID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetGoogleAdsAccountExclusions",
          context: { integrationID, adAccountID },
          tags: { action: "getGoogleAdsAccountExclusions" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startUpdateGoogleAdsAccountExclusions =
  (payload) => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("updateGoogleAdsAccountExclusions")}/updateGoogleAdsAccountExclusions`,
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: data.ok,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUpdateGoogleAdsAccountExclusions",
          context: {
            integrationID: payload?.integrationID,
            adAccountID: payload?.adAccountID,
            section: payload?.section,
          },
          tags: { action: "updateGoogleAdsAccountExclusions" },
          level: "error",
        })
      );
      return {
        ok: false,
        message: error?.response?.data?.message,
      };
    }
  };

export const startGetMerchantsGoogleAds =
  ({ integrationID, adAccountID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getMerchantsGoogleAdsCampaigns")}/getMerchantsGoogleAdsCampaigns`,
        data: {
          integrationID,
          adAccountID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetMerchantProductsGoogleAds",
          context: { integrationID, adAccountID },
          tags: { action: "getMerchantProductsGoogleAds" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startGetMerchantProductsGoogleAds =
  ({ integrationID, adAccountID, merchantCenterID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getMerchantProductsGoogleAdsCampaigns")}/getMerchantProductsGoogleAdsCampaigns`,
        data: {
          integrationID,
          adAccountID,
          merchantCenterID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetLocationsPlatformFromMap",
          context: { integrationID, adAccountID, merchantCenterID },
          tags: { action: "getLocationsPlatformFromMap" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startAddSetLocation = (locationData) => async (dispatch) => {
  try {
    const { pathLocationSets } = dispatch(getPathDatabaseByRole());
    const db = getDatabase();
    const newRef = push(ref(db, pathLocationSets));
    const id_dinamico = newRef.key;

    await set(newRef, {
      name: locationData.name,
      contexts: Array.isArray(locationData.contexts)
        ? locationData.contexts
        : [],
      locations: Array.isArray(locationData.locations)
        ? locationData.locations
        : [],
    });

    return { ok: true, id: id_dinamico };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startAddSetLocation",
        capture: "exception",
      })
    );
    return { ok: false };
  }
};

export const startGetSetLocations = () => async (dispatch) => {
  try {
    const { pathLocationSets } = dispatch(getPathDatabaseByRole());
    const db = getDatabase();
    const locationsRef = ref(db, pathLocationSets);

    const snapshot = await get(locationsRef);

    if (!snapshot.exists()) {
      return { ok: true, data: {} };
    }

    const data = snapshot.val();
    return { ok: true, data };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetSetLocations",
        capture: "exception",
      })
    );
    return { ok: false };
  }
};

export const startGetAdsCampaignsSchemas = () => async (dispatch, getState) => {
  try {
    const schemas = getState()?.adsCampaigns?.schemas;

    if (!_.isEmpty(schemas)) {
      return { ok: true, data: schemas };
    }

    const dbRef = ref(db, `adsCampaignsSchemas`);
    const snapshot = await get(dbRef);
    const data = snapshot.val();

    dispatch(getAdsCampaignsSchemas(data));
    return { ok: true, data };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetAdsCampaignsSchemas",
        capture: "exception",
      })
    );
    return { ok: false };
  }
};

const getAdsCampaignsSchemas = (data) => ({
  type: types.GET_ADS_CAMPAIGNS_SCHEMAS,
  payload: data,
});

export const startGetEstimationReservationAdsCampaign =
  (
    {
      integrationID,
      adAccountID,
      pageID,
      estimationID,
      budget,
      startDate,
      endDate,
      locations,
      genders,
      ageRange,
      interests,
      behaviors,
      demographics,
      customAudiences,
      excludedCustomAudiences,
      frequency,
    },
    abortControllerSignal
  ) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getReservedEstimationAdsCampaigns")}/getReservedEstimationAdsCampaigns`,
        data: {
          integrationID,
          adAccountID,
          pageID,
          estimationID,
          budget,
          startDate,
          endDate,
          locations,
          genders,
          ageRange,
          interests,
          behaviors,
          demographics,
          customAudiences,
          excludedCustomAudiences,
          frequency,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: abortControllerSignal,
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Request aborted");
        return { ok: false, aborted: true };
      }
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetEstimationReservationAdsCampaign",
          context: {
            integrationID,
            adAccountID,
            pageID,
            estimationID,
            budget,
            startDate,
            endDate,
            frequency,
          },
          tags: { action: "getEstimationReservationAdsCampaign" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetEstimationAudienceAdsCampaign =
  (props, abortControllerSignal) => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getAudienceEstimationAdsCampaigns")}/getAudienceEstimationAdsCampaigns`,
        data: props,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: abortControllerSignal,
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Request aborted");
        return { ok: false, aborted: true };
      }
      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetEstimationAudienceAdsCampaign",
          context: {},
          tags: { action: "getEstimationAudienceAdsCampaign" },
          level: "error",
        })
      );
      return errorFormatted;
    }
  };

export const startGetLocationsPlatformFromMap =
  ({ integrationID, adAccountID, locations, platform }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getLocationsPlatformFromMap")}/getLocationsPlatformFromMap`,
        data: {
          integrationID,
          adAccountID,
          locations,
          platform,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: true,
        data: data.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetLocationsPlatformFromMap",
          context: { integrationID, adAccountID, platform },
          tags: { action: "getLocationsPlatformFromMap" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetUserNPSRating = () => async (dispatch) => {
  try {
    const { pathNps } = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, pathNps);
    const snapshot = await get(dbRef);
    const data = snapshot.val();

    if (!data) {
      return { ok: false };
    }

    const {
      isRatedAfterCreate = false,
      isRatedAfterPublish = false,
      isRatedAfterActive7Days = false,
      isRatedAfterActive30Days = false,
      isRatedAfterGenerate9Contents = false,
      lastUpdateTime,
    } = data;

    return {
      ok: true,
      data: {
        isRatedAfterCreate,
        isRatedAfterPublish,
        isRatedAfterActive7Days,
        isRatedAfterActive30Days,
        isRatedAfterGenerate9Contents,
        lastUpdateTime,
      },
    };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetUserNPSRating",
        context: {},
        tags: { action: "getUserNPSRating" },
        level: "error",
      })
    );
    return {
      ok: false,
    };
  }
};

export const startSaveUserNPSRating =
  ({ rating, actionType }) =>
  async (dispatch) => {
    try {
      if (!rating || !actionType) {
        throw new Error(
          "Missing parameters: rating and actionType are required"
        );
      }

      if (rating < 1 || rating > 10) {
        throw new Error("Rating must be between 1 and 10");
      }

      if (
        ![
          NPS_RATING_ACTION_CREATE,
          NPS_RATING_ACTION_PUBLISH,
          NPS_RATING_ACTION_ACTIVE_7_DAYS,
          NPS_RATING_ACTION_ACTIVE_30_DAYS,
          NPS_RATING_ACTION_GENERATE_9_CONTENTS,
        ].includes(actionType)
      ) {
        throw new Error(
          "ActionType must be 'create', 'publish', 'active_7_days', 'active_30_days', or 'generate_9_contents'"
        );
      }

      const { pathNps } = dispatch(getPathDatabaseByRole());

      const npsRef = ref(db, pathNps);
      const snapshot = await get(npsRef);
      const currentData = snapshot.val() || {};

      const isRatedAfterCreate = currentData.isRatedAfterCreate || false;
      const isRatedAfterPublish = currentData.isRatedAfterPublish || false;
      const isRatedAfterActive7Days =
        currentData.isRatedAfterActive7Days || false;
      const isRatedAfterActive30Days =
        currentData.isRatedAfterActive30Days || false;
      const isRatedAfterGenerate9Contents =
        currentData.isRatedAfterGenerate9Contents || false;

      if (actionType === NPS_RATING_ACTION_CREATE && isRatedAfterCreate) {
        throw new Error("User has already rated after creating a campaign");
      }

      if (actionType === NPS_RATING_ACTION_PUBLISH && isRatedAfterPublish) {
        throw new Error("User has already rated after publishing a campaign");
      }

      if (
        actionType === NPS_RATING_ACTION_ACTIVE_7_DAYS &&
        isRatedAfterActive7Days
      ) {
        throw new Error(
          "User has already rated after 7 days of active campaign"
        );
      }

      if (
        actionType === NPS_RATING_ACTION_ACTIVE_30_DAYS &&
        isRatedAfterActive30Days
      ) {
        throw new Error(
          "User has already rated after 30 days of active campaign"
        );
      }

      if (
        actionType === NPS_RATING_ACTION_GENERATE_9_CONTENTS &&
        isRatedAfterGenerate9Contents
      ) {
        throw new Error(
          "User has already rated after generating 9 or more contents"
        );
      }

      const existingRatings = currentData.ratings || [];
      const now = new Date().getTime();

      const newRating = {
        value: rating,
        actionType: actionType,
        timestamp: now,
      };

      existingRatings.push(newRating);

      const totalRatings = existingRatings.reduce((sum, r) => sum + r.value, 0);
      const averageRating = parseFloat(
        (totalRatings / existingRatings.length).toFixed(2)
      );

      const updatedFlags = {
        isRatedAfterCreate:
          actionType === NPS_RATING_ACTION_CREATE ? true : isRatedAfterCreate,
        isRatedAfterPublish:
          actionType === NPS_RATING_ACTION_PUBLISH ? true : isRatedAfterPublish,
        isRatedAfterActive7Days:
          actionType === NPS_RATING_ACTION_ACTIVE_7_DAYS
            ? true
            : isRatedAfterActive7Days,
        isRatedAfterActive30Days:
          actionType === NPS_RATING_ACTION_ACTIVE_30_DAYS
            ? true
            : isRatedAfterActive30Days,
        isRatedAfterGenerate9Contents:
          actionType === NPS_RATING_ACTION_GENERATE_9_CONTENTS
            ? true
            : isRatedAfterGenerate9Contents,
      };

      // Preserve creationTime if it already exists; otherwise set it now
      const creationTime = currentData.creationTime || now;

      const dataToSave = {
        calificacion: averageRating,
        ratings: existingRatings,
        lastUpdateTime: now,
        creationTime,
        totalRatings: existingRatings.length,
        ...updatedFlags,
      };

      await set(npsRef, dataToSave);

      return {
        ok: true,
        data: {
          newRating: rating,
          actionType,
          averageRating,
          totalRatings: existingRatings.length,
          isRatedAfterCreate: updatedFlags.isRatedAfterCreate,
          isRatedAfterPublish: updatedFlags.isRatedAfterPublish,
          isRatedAfterActive7Days: updatedFlags.isRatedAfterActive7Days,
          isRatedAfterActive30Days: updatedFlags.isRatedAfterActive30Days,
          isRatedAfterGenerate9Contents:
            updatedFlags.isRatedAfterGenerate9Contents,
        },
      };
    } catch (error) {
      if (error.code === "PERMISSION_DENIED") {
        return {
          ok: false,
          message:
            "You do not have permission to save this information. Contact the administrator.",
          code: error.code,
        };
      }

      if (error.message && typeof error.message === "string") {
        return {
          ok: false,
          message: error.message,
          code: error.code || "CUSTOM_ERROR",
        };
      }

      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveUserNPSRating",
          context: {},
          tags: { action: "saveUserNPSRating" },
          level: "error",
        })
      );

      return {
        ok: false,
        message: error.message || "Unknown error saving NPS rating",
        code: error.code || "UNKNOWN_ERROR",
      };
    }
  };

const ONBOARDING_CHECKLIST_FLAG_BY_ACTION = {
  [CHECKLIST_CREATE_CAMPAIGN]: "campaigns",
  [CHECKLIST_PUBLISH_CONTENT]: "posts",
  [CHECKLIST_CREATE_CONTENT]: "creatives",
  [CHECKLIST_BRAND_SETUP]: "brandSetup",
};

const ONBOARDING_CHECKLIST_ALREADY_DONE_MESSAGE = {
  [CHECKLIST_CREATE_CAMPAIGN]: "User has already created the first campaign",
  [CHECKLIST_PUBLISH_CONTENT]: "User has already published the first content",
  [CHECKLIST_CREATE_CONTENT]: "User has already created the first IA content",
  [CHECKLIST_BRAND_SETUP]: "User has already completed brand setup",
};

export const startGetUserOnboardingChecklist = () => async (dispatch) => {
  try {
    const { pathOnboardingChecklist } = dispatch(getPathDatabaseByRole());
    const checklistRef = ref(db, pathOnboardingChecklist);
    const snapshot = await get(checklistRef);
    const data = snapshot.val();

    if (!data) {
      return { ok: false };
    }

    const {
      campaigns = false,
      posts = false,
      creatives = false,
      brandSetup = false,
      skipGuide = false,
    } = data;

    return {
      ok: true,
      data: {
        campaigns,
        posts,
        creatives,
        brandSetup,
        skipGuide,
      },
    };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetUserOnboardingChecklist",
        context: {},
        tags: { action: "getUserOnboardingChecklist" },
        level: "error",
      })
    );
    return {
      ok: false,
    };
  }
};

export const startSaveUserOnboardingChecklist =
  ({ actionType }) =>
  async (dispatch) => {
    try {
      if (!actionType) {
        throw new Error("Missing parameter: actionType is required");
      }

      const flagKey = ONBOARDING_CHECKLIST_FLAG_BY_ACTION[actionType];
      if (!flagKey) {
        throw new Error(
          "ActionType must be 'create_first_campaign', 'publish_first_content', 'create_first_ia_content', or 'brand_setup'"
        );
      }

      const { pathOnboardingChecklist } = dispatch(getPathDatabaseByRole());
      const checklistRef = ref(db, pathOnboardingChecklist);
      const snapshot = await get(checklistRef);
      const currentData = snapshot.val() || {};

      const currentFlags = {
        campaigns: currentData.campaigns || false,
        posts: currentData.posts || false,
        creatives: currentData.creatives || false,
        brandSetup: currentData.brandSetup || false,
        skipGuide: currentData.skipGuide || false,
      };

      if (currentFlags[flagKey]) {
        throw new Error(ONBOARDING_CHECKLIST_ALREADY_DONE_MESSAGE[actionType]);
      }

      const updatedFlags = {
        ...currentFlags,
        [flagKey]: true,
      };

      await set(checklistRef, updatedFlags);

      return {
        ok: true,
        data: {
          ...updatedFlags,
        },
      };
    } catch (error) {
      if (error.code === "PERMISSION_DENIED") {
        return {
          ok: false,
          message:
            "You do not have permission to save this information. Contact the administrator.",
          code: error.code,
        };
      }

      if (error.message && typeof error.message === "string") {
        return {
          ok: false,
          message: error.message,
          code: error.code || "CUSTOM_ERROR",
        };
      }

      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveUserOnboardingChecklist",
          context: {},
          tags: { action: "saveUserOnboardingChecklist" },
          level: "error",
        })
      );

      return {
        ok: false,
        message: error.message || "Unknown error saving onboarding checklist",
        code: error.code || "UNKNOWN_ERROR",
      };
    }
  };

export const startSaveOnboardingChecklistSkipGuide = () => async (dispatch) => {
  try {
    const { pathOnboardingChecklist } = dispatch(getPathDatabaseByRole());
    const checklistRef = ref(db, pathOnboardingChecklist);
    const snapshot = await get(checklistRef);
    const currentData = snapshot.val() || {};

    if (currentData.skipGuide) {
      return {
        ok: true,
        data: {
          campaigns: currentData.campaigns || false,
          posts: currentData.posts || false,
          creatives: currentData.creatives || false,
          brandSetup: currentData.brandSetup || false,
          skipGuide: true,
        },
      };
    }

    const updatedFlags = {
      campaigns: currentData.campaigns || false,
      posts: currentData.posts || false,
      creatives: currentData.creatives || false,
      brandSetup: currentData.brandSetup || false,
      skipGuide: true,
    };

    await set(checklistRef, updatedFlags);

    return {
      ok: true,
      data: updatedFlags,
    };
  } catch (error) {
    if (error.code === "PERMISSION_DENIED") {
      return {
        ok: false,
        message:
          "You do not have permission to save this information. Contact the administrator.",
        code: error.code,
      };
    }

    dispatch(
      LaunchError(error, false, [], {
        functionName: "startSaveOnboardingChecklistSkipGuide",
        context: {},
        tags: { action: "saveOnboardingChecklistSkipGuide" },
        level: "error",
      })
    );

    return {
      ok: false,
      message:
        error.message || "Unknown error saving onboarding checklist skip guide",
      code: error.code || "UNKNOWN_ERROR",
    };
  }
};

export const checkAndSaveUserOnboardingChecklist =
  (actionType) => async (dispatch) => {
    try {
      const flagKey = ONBOARDING_CHECKLIST_FLAG_BY_ACTION[actionType];
      if (!flagKey) {
        return;
      }

      const result = await dispatch(startGetUserOnboardingChecklist());
      const alreadyDone = Boolean(result.ok && result.data?.[flagKey]);

      if (!alreadyDone) {
        await dispatch(startSaveUserOnboardingChecklist({ actionType }));
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "checkAndSaveUserOnboardingChecklist",
          capture: "exception",
        })
      );
      console.error("Error checking onboarding checklist status:", error);
    }
  };

export const checkAndShowNpsForm =
  (actionType) => async (dispatch, getState) => {
    try {
      const uid = getState().auth.user?.uid;
      if (isPrivilegedLoginFromStorage({ uid })) {
        return;
      }

      const result = await dispatch(startGetUserNPSRating());

      if (!result.ok || !result.data) {
        dispatch({ type: types.SHOW_NPS_FORM, payload: { actionType } });
        return;
      }

      const {
        isRatedAfterCreate,
        isRatedAfterPublish,
        isRatedAfterActive7Days,
        isRatedAfterActive30Days,
        isRatedAfterGenerate9Contents,
        lastUpdateTime,
      } = result.data;

      // Do not show form if user rated within the last NPS_COOL_DOWN_DAYS
      if (
        lastUpdateTime &&
        differenceInDays(new Date(), new Date(lastUpdateTime)) <
          NPS_COOL_DOWN_DAYS
      ) {
        return;
      }

      if (actionType === "create" && !isRatedAfterCreate) {
        dispatch({ type: types.SHOW_NPS_FORM, payload: { actionType } });
      } else if (actionType === "publish" && !isRatedAfterPublish) {
        dispatch({ type: types.SHOW_NPS_FORM, payload: { actionType } });
      } else if (
        actionType === NPS_RATING_ACTION_ACTIVE_7_DAYS &&
        !isRatedAfterActive7Days &&
        !isRatedAfterActive30Days
      ) {
        dispatch({ type: types.SHOW_NPS_FORM, payload: { actionType } });
      } else if (
        actionType === NPS_RATING_ACTION_ACTIVE_30_DAYS &&
        !isRatedAfterActive30Days
      ) {
        dispatch({ type: types.SHOW_NPS_FORM, payload: { actionType } });
      } else if (
        actionType === NPS_RATING_ACTION_GENERATE_9_CONTENTS &&
        !isRatedAfterGenerate9Contents
      ) {
        dispatch({ type: types.SHOW_NPS_FORM, payload: { actionType } });
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "checkAndShowNpsForm",
          capture: "exception",
        })
      );
      console.error("Error checking NPS status:", error);
    }
  };

export const startSearchAdsCampaignsByName =
  ({ name, limit = 50, minLength = 3 }) =>
  async (dispatch) => {
    try {
      const normalizedName = String(name).toLowerCase().trim();

      if (!normalizedName || normalizedName.length < minLength) {
        return { ok: true, data: {} };
      }

      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("searchAdsCampaignsByName")}/searchAdsCampaignsByName`,
        data: {
          name: normalizedName,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        ok: data.ok,
        data: data.data || {},
      };
    } catch (error) {
      dispatch(LaunchError(error));
      return { ok: false };
    }
  };

export const startGetAdsCampaignRecordsByIDs =
  ({ campaignIDs = [] }) =>
  async (dispatch) => {
    const uniqueCampaignIDs = [
      ...new Set((campaignIDs || []).filter(Boolean).map(String)),
    ];
    if (uniqueCampaignIDs.length === 0) return {};

    try {
      const { pathCampaigns } = dispatch(getPathDatabaseByRole());
      const snapshots = await Promise.all(
        uniqueCampaignIDs.map((campaignID) =>
          get(ref(db, `${pathCampaigns}/${campaignID}`))
        )
      );
      const campaigns = {};
      snapshots.forEach((snapshot, index) => {
        if (!snapshot.exists()) return;
        campaigns[uniqueCampaignIDs[index]] = snapshot.val();
      });
      return campaigns;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdsCampaignRecordsByIDs",
          context: { campaignIDs: uniqueCampaignIDs },
          tags: { action: "getAdsCampaigns" },
          level: "error",
        })
      );
      return {};
    }
  };

const getAdAccountDisplayNameFromIntegrations = ({
  integrations,
  platform,
  integrationID,
  adAccountID,
}) => {
  if (!integrations || !integrationID || !adAccountID) return "";
  const integrationKey = getIntegrationKey(platform, true);
  const integration = integrations[integrationKey]?.[integrationID];
  if (!integration) return "";
  if (platform === META) {
    return integration?.businessAccounts?.[adAccountID]?.name || "";
  }
  return integration?.advertisers?.[adAccountID]?.name || "";
};

export const startGetCampaignPlatformDataForIntegrationNotFoundModal =
  ({ campaignID, platform }) =>
  async (dispatch, getState) => {
    try {
      const { pathCampaigns } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(
        db,
        `${pathCampaigns}/${campaignID}/platformsData/${platform}`
      );
      const snapshot = await get(dbRef);

      if (!snapshot.exists()) {
        return { ok: true, exists: false };
      }

      const data = snapshot.val();
      const { name = "", integrationID = "", adAccountID = "" } = data;
      const integrations = getState()?.user?.integrations;
      const adAccountName = getAdAccountDisplayNameFromIntegrations({
        integrations,
        platform,
        integrationID,
        adAccountID,
      });
      return {
        ok: true,
        exists: true,
        name,
        integrationID,
        adAccountID,
        adAccountName,
      };
    } catch {
      return { ok: false };
    }
  };

export const startUpdateCampaignsPlatformIntegrationFromModal =
  ({ rows, integrationSelections }) =>
  async (dispatch) => {
    try {
      const { pathCampaigns } = dispatch(getPathDatabaseByRole());
      const updates = {};
      const syncItems = [];

      rows.forEach((row, index) => {
        if (row.disabled || row.error) return;
        if (!row.campaignID) return;
        const selectedIntegrationID = integrationSelections[index];
        if (!selectedIntegrationID) return;
        const path = `${pathCampaigns}/${row.campaignID}/platformsData/${row.platform}/integrationID`;
        updates[path] = selectedIntegrationID;
        syncItems.push({
          campaignID: row.campaignID,
          platform: row.platform,
          integrationID: selectedIntegrationID,
        });
      });

      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
      }
      if (syncItems.length > 0) {
        dispatch(setPendingIntegrationFormSync({ items: syncItems }));
      }
      return { ok: true };
    } catch (error) {
      dispatch(LaunchError(error));
      return { ok: false };
    }
  };

export const startGetAdsCampaignsByCreationTimeRange =
  ({ startDateTime, endDateTime }) =>
  async (dispatch) => {
    return dispatch(
      fetchAdsCampaignsByTimeField({
        timeField: "creationTime",
        startDateTime,
        endDateTime,
      })
    );
  };

const mapSnapshotToCampaigns = (snapshot) => {
  const campaigns = {};

  if (snapshot.exists()) {
    snapshot.forEach((child) => {
      campaigns[child.key] = {
        ...child.val(),
        id: child.key,
      };
    });
  }

  return campaigns;
};

const excludeDraftCampaigns = (campaigns = {}) => {
  const filtered = {};

  Object.keys(campaigns || {}).forEach((campaignID) => {
    if (campaigns[campaignID]?.states?.general === ADS_CAMPAIGN_STATUS_DRAFT) {
      return;
    }
    filtered[campaignID] = campaigns[campaignID];
  });

  return filtered;
};

const fetchAdsCampaignsByChildQuery =
  ({ orderByField, startValue, endValue, equalValue }) =>
  async (dispatch) => {
    try {
      const { pathCampaigns } = dispatch(getPathDatabaseByRole());
      const constraints = [orderByChild(orderByField)];

      if (equalValue !== undefined) {
        constraints.push(equalTo(equalValue));
      } else {
        if (startValue !== undefined) {
          constraints.push(startAt(startValue));
        }
        if (endValue !== undefined) {
          constraints.push(endAt(endValue));
        }
      }

      const snapshot = await get(query(ref(db, pathCampaigns), ...constraints));
      return { ok: true, data: mapSnapshotToCampaigns(snapshot) };
    } catch (error) {
      dispatch(LaunchError(error, false));
      return { ok: false, data: {} };
    }
  };

const fetchAdsCampaignsByTimeField =
  ({ timeField, startDateTime, endDateTime, startMs, endMs }) =>
  async (dispatch) => {
    const resolvedStartMs = startMs ?? new Date(startDateTime).getTime();
    const resolvedEndMs = endMs ?? new Date(endDateTime).getTime();

    return dispatch(
      fetchAdsCampaignsByChildQuery({
        orderByField: timeField,
        startValue: resolvedStartMs,
        endValue: resolvedEndMs,
      })
    );
  };

const fetchAdsCampaignsByIds =
  (campaignIDs = []) =>
  async (dispatch) => {
    try {
      const uniqueCampaignIDs = [...new Set(campaignIDs.filter(Boolean))];
      if (!uniqueCampaignIDs.length) {
        return { ok: true, data: {} };
      }

      const { pathCampaigns } = dispatch(getPathDatabaseByRole());
      const snapshots = await Promise.all(
        uniqueCampaignIDs.map((campaignID) =>
          get(ref(db, `${pathCampaigns}/${campaignID}`))
        )
      );
      const campaigns = {};

      snapshots.forEach((snapshot, index) => {
        if (!snapshot.exists()) {
          return;
        }

        const campaignID = uniqueCampaignIDs[index];
        campaigns[campaignID] = {
          ...snapshot.val(),
          id: campaignID,
        };
      });

      return { ok: true, data: campaigns };
    } catch (error) {
      dispatch(LaunchError(error, false));
      return { ok: false, data: {} };
    }
  };

export const mergeCampaignsById = (...campaignMaps) => {
  const merged = {};

  campaignMaps.forEach((campaigns) => {
    Object.keys(campaigns || {}).forEach((campaignID) => {
      merged[campaignID] = {
        ...(merged[campaignID] || {}),
        ...campaigns[campaignID],
        id: campaignID,
      };
    });
  });

  return merged;
};

/**
 * Global Report candidates (not final set): firstActiveTime ≤ end ∪ active ∪
 * creationTime in range. Drafts excluded; activity filter happens after stats.
 */
export const startGetAdsCampaignsForGlobalReport =
  ({ startDateTime, endDateTime }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const includeDemoCampaigns = shouldUseAdsCampaignDemoDateRange({
        advertiserID,
      });
      const startMs = new Date(startDateTime).getTime();
      const endMs = new Date(endDateTime).getTime();

      const [
        firstActiveTimeResponse,
        activeStatusResponse,
        creationTimeResponse,
        demoCampaignsResponse,
      ] = await Promise.all([
        dispatch(
          fetchAdsCampaignsByChildQuery({
            orderByField: "firstActiveTime",
            startValue: 1,
            endValue: endMs,
          })
        ),
        dispatch(
          fetchAdsCampaignsByChildQuery({
            orderByField: "states/general",
            equalValue: ADS_CAMPAIGN_STATUS_ACTIVE,
          })
        ),
        dispatch(
          fetchAdsCampaignsByTimeField({
            timeField: "creationTime",
            startMs,
            endMs,
          })
        ),
        includeDemoCampaigns
          ? dispatch(fetchAdsCampaignsByIds(ADS_CAMPAIGNS_DEMO))
          : Promise.resolve({ ok: true, data: {} }),
      ]);

      const anyOk =
        firstActiveTimeResponse?.ok ||
        activeStatusResponse?.ok ||
        creationTimeResponse?.ok ||
        demoCampaignsResponse?.ok;

      if (!anyOk) {
        return { ok: false, data: {} };
      }

      return {
        ok: true,
        data: excludeDraftCampaigns(
          mergeCampaignsById(
            firstActiveTimeResponse?.data,
            activeStatusResponse?.data,
            creationTimeResponse?.data,
            demoCampaignsResponse?.data
          )
        ),
      };
    } catch (error) {
      dispatch(LaunchError(error, false));
      return { ok: false, data: {} };
    }
  };

export const startGetAdsCampaignsByLastActiveTimeRange =
  ({ startDateTime, endDateTime }) =>
  async (dispatch) => {
    return dispatch(
      fetchAdsCampaignsByTimeField({
        timeField: "lastActiveTime",
        startDateTime,
        endDateTime,
      })
    );
  };

export const startGetActionLogsByCreationTimeRange =
  ({ startDateTime, endDateTime }) =>
  async (dispatch, getState) => {
    try {
      const { agencyID } = dispatch(getPathDatabaseByRole());
      const advertiserID = getState()?.auth?.user?.advertiserID;

      if (!agencyID) {
        return { ok: true, data: [] };
      }

      const startMs = new Date(startDateTime).getTime();
      const endMs = new Date(endDateTime).getTime();

      const dbRef = query(
        ref(db, `actionLogs/${agencyID}`),
        orderByChild("creationTime"),
        startAt(startMs),
        endAt(endMs)
      );
      const snapshot = await get(dbRef);
      const logs = [];

      if (snapshot.exists()) {
        snapshot.forEach((child) => {
          const log = child.val();
          if (advertiserID && log?.advertiserID !== advertiserID) {
            return;
          }
          logs.push({
            id: child.key,
            ...log,
          });
        });
      }

      return { ok: true, data: logs };
    } catch (error) {
      dispatch(LaunchError(error, false));
      return { ok: false, data: [] };
    }
  };

export const startGetAppliedOptimizationsForGlobalReport =
  ({ campaignIDs = [], startDateTime, endDateTime }) =>
  async (dispatch) => {
    try {
      const { pathCampaignLogs } = dispatch(getPathDatabaseByRole());

      const filteredCampaignIDs = campaignIDs.filter(
        (campaignID) => !ADS_CAMPAIGNS_DEMO.includes(campaignID)
      );

      if (!filteredCampaignIDs.length) {
        return { ok: true, data: { count: 0, latestCreationTime: 0 } };
      }

      const startMs = new Date(startDateTime).getTime();
      const endMs = new Date(endDateTime).getTime();

      const snapshots = await Promise.all(
        filteredCampaignIDs.map((campaignID) =>
          get(
            query(
              ref(db, `${pathCampaignLogs}/${campaignID}`),
              orderByChild("creationTime"),
              startAt(startMs),
              endAt(endMs)
            )
          )
        )
      );

      let count = 0;
      let latestCreationTime = 0;

      snapshots.forEach((snapshot) => {
        if (!snapshot.exists()) return;

        const result = countAppliedOptimizationsFromCampaignLogs({
          campaignLogsMap: snapshot.val(),
          startDateTime,
          endDateTime,
        });

        count += result.count;
        if (result.latestCreationTime > latestCreationTime) {
          latestCreationTime = result.latestCreationTime;
        }
      });

      return { ok: true, data: { count, latestCreationTime } };
    } catch (error) {
      dispatch(LaunchError(error, false));
      return { ok: false, data: { count: 0, latestCreationTime: 0 } };
    }
  };

export const startGetMetaReusableCreatives =
  ({
    advertiserID,
    adAccountID,
    pageID,
    lastKey,
    onChangeLastKey = () => {},
    onChangeLoadLastItem = () => {},
    hashUrl = null,
    limit = 15,
  }) =>
  async (dispatch) => {
    try {
      if (!advertiserID || !adAccountID || !pageID) {
        onChangeLoadLastItem(true);
        return { ok: false, data: [] };
      }

      const dbRef = ref(
        db,
        `creativesByAdAccountAndPage/${advertiserID}/${adAccountID}/${pageID}`
      );

      let q;
      if (hashUrl) {
        const prefixStart = `${hashUrl}_`;
        const prefixEnd = `${hashUrl}_\uf8ff`;
        const base = query(dbRef, orderByChild("creationTimeHashUrl"));
        if (lastKey?.value != null && typeof lastKey?.id === "string") {
          q = query(
            base,
            startAfter(lastKey.value, lastKey.id),
            endAt(prefixEnd),
            limitToFirst(limit)
          );
        } else {
          q = query(
            base,
            startAt(prefixStart),
            endAt(prefixEnd),
            limitToFirst(limit)
          );
        }
      } else {
        const base = query(dbRef, orderByChild("creationTimeNegative"));
        if (lastKey?.value != null && typeof lastKey?.id === "string") {
          q = query(
            base,
            startAfter(lastKey.value, lastKey.id),
            limitToFirst(limit)
          );
        } else {
          q = query(base, limitToFirst(limit));
        }
      }

      const snapshot = await get(q);
      if (!snapshot.exists()) {
        onChangeLoadLastItem(true);
        return { ok: true, data: [] };
      }

      const data = [];
      snapshot.forEach((child) => {
        data.push({ key: child.key, creativeID: child.key, ...child.val() });
      });

      if (data.length < limit) {
        onChangeLoadLastItem(true);
      }

      if (data.length > 0) {
        const lastItem = data[data.length - 1];
        onChangeLastKey({
          value: hashUrl
            ? lastItem.creationTimeHashUrl
            : lastItem.creationTimeNegative,
          id: lastItem.key,
        });
      }

      return { ok: true, data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetMetaReusableCreatives",
        })
      );
      onChangeLoadLastItem(true);
      return { ok: false, data: [] };
    }
  };

export const startSyncMetaCreativeEngagements =
  ({ advertiserID, adAccountID, pageID, integrationID, creativeIDs = [] }) =>
  async (dispatch) => {
    try {
      if (
        !advertiserID ||
        !adAccountID ||
        !pageID ||
        !integrationID ||
        !creativeIDs.length
      ) {
        return { ok: false, data: {} };
      }

      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("syncMetaCreativeEngagements")}/syncMetaCreativeEngagements`,
        data: {
          advertiserID,
          adAccountID,
          pageID,
          integrationID,
          creativeIDs,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return { ok: false, data: {} };
      }

      return { ok: true, data: data.data || {} };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSyncMetaCreativeEngagements",
        })
      );
      return { ok: false, data: {} };
    }
  };

export const startRefreshGoogleSearchTerms =
  ({ idPlatform, integrationID, adAccountID }) =>
  async (dispatch) => {
    try {
      if (!idPlatform || !integrationID || !adAccountID) {
        return { ok: false };
      }

      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getSearchTermsAdsCampaigns")}/getSearchTermsAdsCampaigns`,
        data: {
          idPlatform,
          integrationID,
          adAccountID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data.ok) {
        return { ok: false };
      }

      return { ok: true, data: data.data || {} };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRefreshGoogleSearchTerms",
          context: { idPlatform, integrationID, adAccountID },
          tags: { action: "getSearchTermsAdsCampaigns" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
